import { TagColorEnum } from "@/__generated__/graphql";
import {
  Fragment,
  useContext,
  useState,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  memo,
  type ComponentProps,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CornerDownRightIcon,
  FileWarningIcon,
  LayersIcon,
  MailIcon,
  PanelRightOpenIcon,
  SparklesIcon,
  XIcon,
  type LucideIcon,
} from "lucide-react";
import { MicrosoftIcon } from "@/objects/@icons";

import {
  ExternalProviderEnum,
  RuleDefinitionSuggestionTypeEnum,
  SettlementTreatmentEnum,
  TriageListItemFragment,
  TriageSuggestionStatusEnum,
} from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { icons, iconsByTypename } from "@/objects/@icons";
import { EnumColors, TagColors } from "@/constants/values";
import { useEnumColor } from "@/hooks/useEnumColor";
import { SelectableCardListSearchField } from "@/components/molecules/SelectableCardList";
import { getSelectableRowInset } from "@/components/molecules/SelectableRow";
import { useSelectableListStyle } from "@/components/molecules/SelectableList";
import { BoxForOrganization } from "@/components/atoms/BoxForOrganization";
import { identifierAndTypenameToId } from "@/objects/@utils";
import {
  findNearestContextNode,
  findDeepestOpenResetHotkeysDescendant,
  hasOpenResetHotkeysOverlayInSubtree,
  isAncestorOrSelf,
  useContextScope,
} from "@/hooks/useContextTree";
import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { State } from "@/state";
import { Utils } from "@/utils";
import type { FieldImperativeHandle } from "@/components/@types";
import { FieldReferences } from "@/components/organisms/Field/FieldReferences";
import { getReferenceValueOptionsByTypename } from "@/objects";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { ButtonGroup, ButtonGroupText } from "@/components/ui/button-group";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

import {
  TriageRowPickedContext,
  useTriageViewActions,
  type TriageActiveRowStaging,
  type TriageBulkMatch,
  type TriageSelectedTag,
} from "./TriageView.context";
import { JOB_ICONS, TRIAGE_KEYS, type TriageJobEnum } from "./TriageView.keys";
import {
  TRIAGE_RECOMMENDATION_NOUNS,
  TRIAGE_SETTLEMENTS,
} from "./TriageView.content";
import { TriageMovePayment } from "./TriageMovePayment";

export const TRIAGE_RECONCILE_ROW_SKELETON_COUNT = 4;

/**
 * What the queue's rows are while it loads: a typical payment, drawn as any
 * is (``isLoading``) - so a loading row is the size of a loaded one.
 */
export const TRIAGE_RECONCILE_ROW_PLACEHOLDER: TriageListItemFragment = {
  id: "placeholder",
  identifier: "placeholder",
  description: "Northwind Supplies",
  payeePaymentCount: 0,
  paymentFor: [],
  amount: "-1250.00",
  effectiveDate: "2026-01-15",
  triageHeldAt: null,
  triageHoldReason: null,
  triageHeldForViewer: false,
  isProcessing: false,
  isBooked: false,
  emailedDocuments: [],
  entity: {
    id: "placeholder-entity",
    identifier: "placeholder-entity",
    name: "Northwind Traders",
  },
  financialAccount: {
    id: "placeholder-account",
    identifier: "placeholder-account",
    name: "Operating account",
    chartedAccount: {
      id: "placeholder-charted-account",
      identifier: "placeholder-charted-account",
      name: "Operating account",
      path: "",
      fullPath: [],
    },
  },
  chartedAccount: null,
  belongsToEntity: null,
  tags: [],
  // A typical row's: a document suggested, and an account the AI would post
  // it to instead - two ways.
  suggestions: [
    RuleDefinitionSuggestionTypeEnum.MatchDocument,
    RuleDefinitionSuggestionTypeEnum.Categorize,
  ].map((job, index) => ({
    id: `placeholder-suggestion-${index}`,
    identifier: `placeholder-suggestion-${index}`,
    status: TriageSuggestionStatusEnum.Pending,
    job,
    source: index === 1 ? "ai" : "rule",
    confidence: "0.8",
    rule: null,
    ruleSuggestionDocumentOptions: [],
    lines: [],
  })),
};

/**
 * A chip in a search, drawn as a set filter's chip is - one of shadcn's button
 * groups - at a row's size, in the search's line: what it narrows to, then its
 * x - or, while it works, a spinner in the x's place. One with a mark is its
 * mark alone, its words said by its tooltip and to a reader (``sr-only``); a
 * typed term, with none, is its words.
 */
export function TriageSearchChip({
  startDecorator,
  children,
  removeLabel,
  onRemove,
  removeTestId,
  isBusy = false,
  className,
  ref,
  ...rest
}: {
  startDecorator?: ReactNode;
  children?: ReactNode;
  removeLabel?: string;
  /** Its x, letting it go - none without it. */
  onRemove?: () => void;
  removeTestId?: string;
  isBusy?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  "data-testid"?: string;
  "aria-label"?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  // Its mark, a square of its own; its words, unless it has none.
  const what = startDecorator ? (
    <ButtonGroupText className="w-8 justify-center px-0">
      {startDecorator}
      {children ? <span className="sr-only">{children}</span> : null}
    </ButtonGroupText>
  ) : (
    // On its one line, its words cut short where the search has not the
    // room - at a word where one fits - never broken onto a second over what
    // is typed. Free to break anywhere, it can go as short as a letter.
    <ButtonGroupText className="min-w-0 gap-1 px-3">
      {children ? (
        <span className="line-clamp-1 wrap-anywhere">{children}</span>
      ) : null}
    </ButtonGroupText>
  );
  return (
    <ButtonGroup
      {...rest}
      ref={ref}
      data-search-chip=""
      // ``h-8``, ``sm``'s: the bar's buttons' height, as its x is theirs.
      className={cn("h-8 min-w-0 self-center", className)}
    >
      {/* Not a control of its own: its x is. A mark alone is named by its
          tooltip - unless what holds it says more in one of its own. */}
      {startDecorator && children && !rest["aria-label"] ? (
        <Tooltip>
          <TooltipTrigger asChild>{what}</TooltipTrigger>
          <TooltipContent side="bottom">{children}</TooltipContent>
        </Tooltip>
      ) : (
        what
      )}
      {isBusy ? (
        <ButtonGroupText className="w-8 justify-center px-0">
          <Spinner aria-label="Searching the mailboxes" />
        </ButtonGroupText>
      ) : !onRemove ? null : (
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={removeLabel}
          data-testid={removeTestId}
          onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
          onClick={onRemove}
        >
          <XIcon />
        </Button>
      )}
    </ButtonGroup>
  );
}

/**
 * The match panel's search: what is typed made a chip by Enter, each card
 * searched for its own. The card the keyboard is on is a chip in it - and, on
 * the Match card, the mailboxes searched too - each let go by its x, or the
 * last by Backspace at its start. Tab brings the keyboard in from the
 * suggestions; Tab or Escape takes it back.
 */
export function TriageResolutionSearchField({
  placeholder,
  value,
  onChange,
  containerClassName,
  disabled = false,
  scope = null,
  onClearScope,
  inputTestId,
  onStep,
  mailboxes = null,
  onClearMailboxes,
  isSearchingMailboxes = false,
  terms = [],
  onAddTerm,
  onRemoveTerm,
  offer = null,
  onFocusChange,
  canTabIn = false,
  onTabOut,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  containerClassName?: string;
  disabled?: boolean;
  /** What it is narrowed to - the card the keyboard is on, or the payment - by its words and its icon. */
  scope?: { title: string; icon: LucideIcon } | null;
  onClearScope?: () => void;
  inputTestId?: string;
  /** The arrow keys typed here: the suggestions' keyboard stepped down or up. */
  onStep?: (by: 1 | -1) => void;
  /**
   * On the Match card, the addresses of the mailboxes what is typed is looked
   * for in as it is typed, as a chip of its own; its x lets them go.
   */
  mailboxes?: Array<{
    emailAddress: string;
    provider?: ExternalProviderEnum | null;
  }> | null;
  onClearMailboxes?: () => void;
  isSearchingMailboxes?: boolean;
  /** Words searched for besides what is typed: each a chip, made by Enter, its x letting it go. */
  terms?: string[];
  onAddTerm?: (term: string) => void;
  onRemoveTerm?: (index: number) => void;
  /** A word searched for on another card, filled in by Tab while nothing is typed. */
  offer?: string | null;
  /** The keyboard come into it, or gone from it. */
  onFocusChange?: (focused: boolean) => void;
  /** Tab brings the keyboard into it from where it is now: said in its place, away from it. */
  canTabIn?: boolean;
  /** Tab out of it, with nothing to fill in: the keyboard there - the queue's search - not back where it came from. */
  onTabOut?: () => void;
}) {
  // Its own scope, keeping what is typed there: the suggestions' single-key
  // shortcuts ("r" to reject, "v" to view, "m" to move) are the list's, not
  // its search's.
  const { ref: scopeRef } = useContextScope({ capturesInput: true });
  // Where the keyboard was before it came in - the suggestions - to go back
  // to as it leaves.
  const cameFromRef = useRef<HTMLElement | null>(null);
  return (
    <div
      ref={scopeRef}
      onFocus={(event) => {
        if (
          event.relatedTarget instanceof HTMLElement &&
          !event.currentTarget.contains(event.relatedTarget)
        ) {
          cameFromRef.current = event.relatedTarget;
        }
        onFocusChange?.(true);
      }}
      onBlur={(event) => {
        // Gone from it - not on to a chip's x inside it.
        if (
          !(event.relatedTarget instanceof Node) ||
          !event.currentTarget.contains(event.relatedTarget)
        ) {
          onFocusChange?.(false);
        }
      }}
      className={cn(
        "contents",
        // Its words a button's words' inset (``sm``'s 12px) from what is
        // before them - the field's start, or what it is narrowed to - and
        // none kept after them.
        "[&_[data-slot='input-group']_[data-slot='input-group-control']]:pl-3 [&_[data-slot='input-group']_[data-slot='input-group-control']]:pr-0",
        // Where Tab brings the keyboard in, that is said in its place away
        // from it; else what it searches.
        canTabIn &&
          "[&_[data-slot='input-group']:not(:has([data-slot='input-group-control']:focus))_input::placeholder]:text-transparent",
        // Nothing typed, the box the hint stands in for takes none of its
        // line: the hint has all of it.
        canTabIn &&
          !value &&
          "[&_[data-slot='input-group']:not(:has([data-slot='input-group-control']:focus))_input]:flex-[0_0_0px] [&_[data-slot='input-group']:not(:has([data-slot='input-group-control']:focus))_input]:min-w-0 [&_[data-slot='input-group']:not(:has([data-slot='input-group-control']:focus))_input]:pl-0",
      )}
    >
      <SelectableCardListSearchField
        // A field on the panel's bar, beside its controls.
        align="field"
        // What it searches for, said in its words.
        hasGlass={false}
        // Said once in it; away from it, how to come back says it instead.
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        containerClassName={containerClassName}
        inputTestId={inputTestId}
        onKeyDown={(event) => {
          // Nothing typed, what another card was searched for: filled in by
          // Tab, for Enter to search for here too.
          if (event.key === "Tab" && !event.shiftKey && offer && !value) {
            event.preventDefault();
            onChange(offer);
            return;
          }
          // Tab, to the other search where there is one.
          if (event.key === "Tab" && !event.shiftKey && onTabOut) {
            event.preventDefault();
            onTabOut();
            return;
          }
          // Left by Tab, as it came in, or Escape: the keyboard back where it
          // was, what is typed kept.
          if (
            (event.key === "Tab" && !event.shiftKey) ||
            event.key === "Escape"
          ) {
            event.preventDefault();
            if (cameFromRef.current?.isConnected) cameFromRef.current.focus();
            else event.currentTarget.blur();
            return;
          }
          // The caret moved along what is typed - not the panel left.
          if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
            event.stopPropagation();
            return;
          }
          if (
            (event.key === "ArrowDown" || event.key === "ArrowUp") &&
            onStep
          ) {
            event.preventDefault();
            onStep(event.key === "ArrowDown" ? 1 : -1);
            return;
          }
          // What is typed, a chip of its own: searched for with the rest.
          if (event.key === "Enter" && value.trim() && onAddTerm) {
            event.preventDefault();
            onAddTerm(value.trim());
            return;
          }
          // At its start, the chip before it let go: the last word, then the
          // mailboxes, then the kind it is narrowed to.
          if (
            event.key === "Backspace" &&
            event.currentTarget.selectionStart === 0 &&
            event.currentTarget.selectionEnd === 0
          ) {
            const letGo =
              terms.length > 0
                ? () => onRemoveTerm?.(terms.length - 1)
                : scope && mailboxes?.length
                  ? onClearMailboxes
                  : scope
                    ? onClearScope
                    : undefined;
            if (letGo) {
              event.preventDefault();
              letGo();
            }
          }
        }}
        leading={
          <>
            {scope ? (
              <TriageSearchChip
                data-testid="triage-suggestions-scope-chip"
                // Its card's icon, as the card's head draws it.
                startDecorator={<Icon ReactIcon={scope.icon} size="md" />}
                removeLabel={`Not just ${scope.title}`}
                onRemove={onClearScope}
                className="shrink-0"
              >
                {scope.title}
              </TriageSearchChip>
            ) : null}
            {scope && mailboxes?.length ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <TriageSearchChip
                    data-testid="triage-suggestions-mailboxes-chip"
                    aria-label={`Searching ${
                      mailboxes.length > 1
                        ? `${mailboxes
                            .slice(0, -1)
                            .map((mailbox) => mailbox.emailAddress)
                            .join(
                              ", ",
                            )} and ${mailboxes[mailboxes.length - 1].emailAddress}`
                        : mailboxes[0].emailAddress
                    }`}
                    // Whose mailbox it is, by its provider's own mark.
                    startDecorator={
                      mailboxes[0].provider === ExternalProviderEnum.Outlook ? (
                        <Icon ReactIcon={MicrosoftIcon} size="md" />
                      ) : (
                        <img
                          src="/svgs/integrations/gmail.svg"
                          alt=""
                          className="w-4 h-4 block"
                        />
                      )
                    }
                    // Being searched, the spinner in its x's place.
                    isBusy={isSearchingMailboxes}
                    removeLabel="Not in the mailboxes"
                    onRemove={onClearMailboxes}
                    className="shrink-0 ml-1.5"
                  >
                    {mailboxes.length > 1 ? `+${mailboxes.length - 1}` : null}
                  </TriageSearchChip>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start">
                  {mailboxes.map((mailbox) => mailbox.emailAddress).join(", ")}
                </TooltipContent>
              </Tooltip>
            ) : null}
            {terms.map((term, index) => (
              <TriageSearchChip
                key={`${index}:${term}`}
                data-testid="triage-suggestions-term-chip"
                removeLabel={`Not “${term}”`}
                onRemove={() => onRemoveTerm?.(index)}
                className={cn("shrink", (index > 0 || scope) && "ml-1.5")}
              >
                “{term}”
              </TriageSearchChip>
            ))}
            {/* Away from it with nothing typed, in what it would say once
                  in it: how to come back. Light, and gone once in it. */}
            {value || !canTabIn ? null : (
              <Typography
                level="body-md"
                data-testid="triage-suggestions-search-hint"
                // A click on it is a click into the box it stands in.
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.currentTarget
                    .closest('[data-slot="input-group"]')
                    ?.querySelector("input")
                    ?.focus();
                }}
                // On the line the chips are on, in what room they leave -
                // its words cut short, not put on a line of their own.
                className={cn(
                  "flex items-center gap-1.5 whitespace-nowrap text-foreground opacity-[0.64] flex-[1_1_0] min-w-0",
                  // As far in as the words it stands for.
                  "ml-3",
                  "cursor-text [[data-slot='input-group']:has([data-slot='input-group-control']:focus)_&]:hidden",
                )}
              >
                <HotkeyDecorator hotkey="tab" color="inherit" />
                {/* What the box says once in it - "Search for documents…" -
                    and how to get there. */}
                <span className="min-w-0 whitespace-normal line-clamp-1 wrap-anywhere">
                  {`Press tab to ${placeholder.charAt(0).toLowerCase()}${placeholder.slice(1)}`}
                </span>
              </Typography>
            )}
          </>
        }
        // Light, at its end, in it: with words typed, how to search for them;
        // with none, what Tab fills in.
        endDecorator={
          !value.trim() && offer ? (
            <Typography
              level="body-md"
              data-testid="triage-suggestions-search-offer"
              className="hidden items-center gap-1.5 whitespace-nowrap text-foreground opacity-[0.64] [[data-slot='input-group']:has([data-slot='input-group-control']:focus)_&]:flex"
            >
              <HotkeyDecorator hotkey="tab" color="inherit" />“{offer}”
            </Typography>
          ) : value.trim() && onAddTerm ? (
            <Typography
              level="body-md"
              data-testid="triage-suggestions-search-enter-hint"
              className="hidden items-center gap-1.5 whitespace-nowrap text-foreground opacity-[0.64] [[data-slot='input-group']:has([data-slot='input-group-control']:focus)_&]:flex"
            >
              <HotkeyDecorator hotkey="enter" color="inherit" />
              to search
            </Typography>
          ) : undefined
        }
      />
    </div>
  );
}

function getTriageRowFocusables(row: Element): HTMLElement[] {
  return Array.from(
    row.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => {
    if (element.getAttribute("aria-hidden") === "true") {
      return false;
    }
    // ``button``/``a`` matchers ignore tabindex; still skip explicitly removed stops.
    if (element.tabIndex < 0) {
      return false;
    }
    // Skip elements that are not visible / not tabbable in practice.
    return element.getClientRects().length > 0;
  });
}

function moveTriageRowFocus(
  row: Element,
  direction: "up" | "down",
): "moved" | "at-start" | "at-end" | "none" {
  const focusables = getTriageRowFocusables(row);
  if (focusables.length === 0) {
    return "none";
  }
  const active = document.activeElement as HTMLElement | null;
  const index = active ? focusables.indexOf(active) : -1;
  if (index < 0) {
    return "none";
  }
  const nextIndex = direction === "down" ? index + 1 : index - 1;
  if (nextIndex < 0) {
    return "at-start";
  }
  if (nextIndex >= focusables.length) {
    return "at-end";
  }
  focusables[nextIndex].focus();
  return "moved";
}

type TriageRowDetailBridge = {
  triage: TriageActiveRowStaging["triage"];
  selections: TriageActiveRowStaging["selections"];
  recommendedTreatment: TriageActiveRowStaging["recommendedTreatment"];
  movePayment: TriageActiveRowStaging["movePayment"];
  movingPaymentTo: TriageActiveRowStaging["movingPaymentTo"];
  isProcessing: TriageActiveRowStaging["isProcessing"];
  isSimilar: TriageActiveRowStaging["isSimilar"];
  setTags: TriageActiveRowStaging["setTags"];
};

function toRowDetailBridge(
  rowStaging: TriageActiveRowStaging | null | undefined,
  triageIdentifier: string,
): TriageRowDetailBridge | null {
  if (!rowStaging || rowStaging.triageIdentifier !== triageIdentifier) {
    return null;
  }
  return {
    triage: rowStaging.triage,
    selections: rowStaging.selections,
    recommendedTreatment: rowStaging.recommendedTreatment,
    movePayment: rowStaging.movePayment,
    movingPaymentTo: rowStaging.movingPaymentTo,
    isProcessing: rowStaging.isProcessing,
    isSimilar: rowStaging.isSimilar,
    setTags: rowStaging.setTags,
  };
}

/**
 * A payment's tags, at its row's end beside its panel's button: on the one
 * open, those it is booked with - changed here, or opened by X from the queue
 * or the suggestions alike - and on the others, its own.
 */
function TriageRowTags({
  item,
  detailContext,
}: {
  item: TriageListItemFragment;
  detailContext: TriageRowDetailBridge | null;
}) {
  const field = useRef<FieldImperativeHandle>(null);
  // Where the keyboard was as X opened them - the queue or the suggestions -
  // given it back as they shut.
  const focusedRef = useRef<HTMLElement | null>(null);
  const { registerOpenTags } = useTriageViewActions();
  const isOpen = detailContext != null;
  // Booked into another business, its tags are its row's there: that business's.
  const tags: TriageSelectedTag[] = isOpen
    ? detailContext.selections.selectedTags
    : ((item.movedToTransaction ?? item).tags ?? []).flatMap((tag) =>
        tag?.id && tag.identifier
          ? [
              {
                __typename: "TagObject" as const,
                id: tag.id,
                identifier: tag.identifier,
                name: tag.name ?? "",
                color: tag.color,
              },
            ]
          : [],
      );
  useEffect(() => {
    if (!isOpen) return;
    registerOpenTags(() => {
      focusedRef.current = document.activeElement as HTMLElement | null;
      field.current?.openSelect?.();
    });
    return () => registerOpenTags(null);
  }, [isOpen, registerOpenTags]);
  if (!isOpen && tags.length === 0) return null;
  // On the one open, what X does here, said as the pointer is on it.
  const tagsField = (
    <div
      data-testid="triage-row-tags"
      data-open={isOpen || undefined}
      // Its own: a tag added is not a press of the row.
      onClick={(event) => event.stopPropagation()}
      // Its + soft, the size of a button at a card's foot and the roundness of
      // the panel's button beside it. In the half under a row, a size down
      // with its chips: as tall as their line, which it never makes taller.
      className={cn(
        "flex items-center min-w-0 [&_[data-testid='field-reference-add-button']]:[border:none] [&_[data-testid='field-reference-add-button']]:bg-secondary [&_[data-testid='field-reference-add-button']]:text-secondary-foreground [&_[data-testid='field-reference-add-button']]:w-6.5 [&_[data-testid='field-reference-add-button']]:h-6.5 [&_[data-testid='field-reference-add-button']]:min-w-6.5 [&_[data-testid='field-reference-add-button']]:min-h-6.5 [&_[data-testid='field-reference-add-button']]:rounded-lg [&_[data-testid='field-reference-add-button']]:[&:hover]:bg-secondary/80",
        "[[data-triage-row-half]_&]:[&_[data-testid='field-reference-add-button']]:w-5 [[data-triage-row-half]_&]:[&_[data-testid='field-reference-add-button']]:h-5 [[data-triage-row-half]_&]:[&_[data-testid='field-reference-add-button']]:min-w-5 [[data-triage-row-half]_&]:[&_[data-testid='field-reference-add-button']]:min-h-5",
      )}
    >
      <FieldReferences
        ref={field}
        value={tags}
        valueOptions={getReferenceValueOptionsByTypename("TagObject")(
          [
            (item.movedToTransaction ? item.belongsToEntity : item.entity)
              ?.identifier,
          ].filter((identifier): identifier is string => Boolean(identifier)),
        )}
        events={
          isOpen
            ? {
                onChangeField: (next: unknown) =>
                  detailContext.setTags(
                    (Array.isArray(next) ? next : []).filter(
                      (tag): tag is TriageSelectedTag =>
                        Boolean(tag?.id && tag?.identifier),
                    ),
                  ),
                onDeleteField: (index?: number) =>
                  detailContext.setTags(
                    typeof index === "number"
                      ? tags.filter((_, at) => at !== index)
                      : [],
                  ),
              }
            : undefined
        }
        focusRestoreRef={focusedRef}
        isMultiline={false}
        size="sm"
        includeAddButton={isOpen}
      />
    </div>
  );
  return isOpen ? (
    <Tooltip>
      <TooltipTrigger asChild>{tagsField}</TooltipTrigger>
      <TooltipContent side="top">
        <div className="flex items-center gap-1.5">
          Tags
          <HotkeyDecorator hotkey={TRIAGE_KEYS.TAGS} color="inherit" />
        </div>
      </TooltipContent>
    </Tooltip>
  ) : (
    tagsField
  );
}

type TriageBelongsToEntity = {
  __typename: "EntityObject";
  id: string;
  identifier: string;
  name: string;
};

/** Another business's half of a payment, as what it is lined up with books it there: whose, and what it is there. */
export type TriageRowHalf = {
  entity: {
    id?: string | null;
    identifier?: string | null;
    name?: string | null;
  };
  says: string;
};

function toBelongsToEntity(
  entity:
    | {
        id?: string | null;
        identifier?: string | null;
        name?: string | null;
      }
    | null
    | undefined,
): TriageBelongsToEntity | null {
  if (!entity?.id || !entity.identifier) {
    return null;
  }
  return {
    __typename: "EntityObject",
    id: entity.id,
    identifier: entity.identifier,
    name: entity.name ?? "",
  };
}

function resolveRowSettlementEntity(
  item: TriageListItemFragment,
  detailContext: TriageRowDetailBridge | null,
  isMatchingDetail: boolean,
): TriageBelongsToEntity | null {
  const payerEntityId = item.entity?.identifier ?? null;
  if (!payerEntityId) {
    return null;
  }

  if (isMatchingDetail && detailContext) {
    // The suggestion picked for it, previewed: booked in another business, the
    // payment's row shows that business's half, as accepting it would.
    const picked = detailContext.triage.suggestions?.find(
      (suggestion) =>
        suggestion.identifier ===
        detailContext.selections.linesSuggestionIdentifier,
    );
    const theirs = picked?.lines?.find(
      (line) =>
        line.entity?.identifier && line.entity.identifier !== payerEntityId,
    )?.entity;
    if (theirs) {
      return toBelongsToEntity(theirs);
    }
    // Said by its Move to be another business's, as its detail now reads.
    const moved = detailContext.triage.belongsToEntity;
    if (moved?.identifier) {
      return toBelongsToEntity(moved);
    }
    const stagedId = detailContext.selections.settlementEntityIdentifier;
    if (stagedId && stagedId !== payerEntityId) {
      const stagedEntity = State.getState().entities.find(
        (entity) => entity.identifier === stagedId,
      );
      return toBelongsToEntity(stagedEntity);
    }
  }

  // Said by its Move to be another business's, or booked into one: that
  // business's half, as booking it writes or wrote.
  const movedTo = item.belongsToEntity;
  return movedTo?.identifier ? toBelongsToEntity(movedTo) : null;
}

/** How far down a payment's card the middle of its amount is, its lead line above it: what sits beside a card - a tick, a pin, an x - is level with it. */
export const TRIAGE_ROW_AMOUNT_MIDDLE = 53.5;

// Its two lines: what it is from and its date over its title and amount -
// Similar's bulk list sets its rows on the same.
// Mirrored, its two columns change sides - each piece in them still read left
// to right.
export const TRIAGE_RECONCILE_ROW_GRID_CLASS =
  "w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0 pt-0.5 items-center [[data-triage-mirrored]_&]:[direction:rtl] [[data-triage-mirrored]_&]:[&_>_*]:[direction:ltr]";

/**
 * What is set in under a payment or a suggestion for one, beside a return
 * arrow pointing at its first line, divider and all, so it reads as part of
 * what is above it: another business's half - whose it is, and what it is
 * there - or another payment its document is paid by, as the queue's row has
 * a payment.
 */
export function TriageReconcileRowMirror({
  children,
  ...half
}: Parameters<typeof TriageReconcileRowMirrorHalf>[0] & {
  /** Under it: on a card of several ways to post it there, each way; under another payment, what else it pays; a moved payment's row of tools. */
  children?: ReactNode;
}) {
  return (
    <div
      // What is on it a size down from the row above it
      // (``TRIAGE_ROW_FOOT_CHIP_CLASS``, ``TriageCardTools``).
      data-triage-row-half=""
      // Mirrored, the arrow comes in from the other side.
      className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-1.5 [align-items:start] ml-2 [[data-triage-mirrored]_&]:[direction:rtl] [[data-triage-mirrored]_&]:ml-0 [[data-triage-mirrored]_&]:mr-2 [[data-triage-mirrored]_&]:[&_>_*]:[direction:ltr]"
    >
      <div className="col-2 [border-top-width:1px] [border-top-style:solid] border-border mt-3 mb-3" />
      <div
        aria-hidden
        data-testid="triage-row-mirror-arrow"
        // As tall as the first line, so the arrow sits centred on it.
        className="flex items-center h-5 pt-0.5 text-border [line-height:0] [[data-triage-mirrored]_&]:[transform:scaleX(-1)]"
      >
        <Icon ReactIcon={CornerDownRightIcon} size="sm" />
      </div>
      <div className="min-w-0">
        <TriageReconcileRowMirrorHalf {...half} />
        {children}
      </div>
    </div>
  );
}

/** A change staged on a payment's half: whose it was and how the two settled it, struck out, then as it becomes - either none where there is none. */
export type TriageRowHalfStaged = {
  was: { name: string | null; says: string } | null;
  becomes: { name: string | null; says: string } | null;
};

function TriageReconcileRowMirrorHalf({
  settlementEntity,
  lead = "For",
  account = null,
  description,
  descriptionNodeId = null,
  formattedAmount,
  formattedDate,
  staged = null,
}: {
  /** The change picked, staged: drawn from what it is to what it becomes, in place of whose it is and what it is there. */
  staged?: TriageRowHalfStaged | null;
  settlementEntity: { id: string | null; name: string };
  /** The word before whose it is: "For" its half there, or another of it. */
  lead?: string;
  /** The bank account it is on, after the business - as the queue's row says. */
  account?: { id: string | null; name: string } | null;
  /** What it is there - said by each way under it, when there are several. */
  description?: string;
  /** The row it is, opened from its words. */
  descriptionNodeId?: string | null;
  /** Its amount and date, when they are not said above it. */
  formattedAmount?: string;
  formattedDate?: string;
}) {
  const settlementName = settlementEntity.name?.trim() || null;
  const isNameStaged =
    staged != null && staged.was?.name !== staged.becomes?.name;

  return (
    <div
      data-testid="triage-row-mirror-half"
      className={TRIAGE_RECONCILE_ROW_GRID_CLASS}
    >
      <div className="col-1 flex items-center min-w-0 gap-1 font-semibold text-[11px] leading-snug text-muted-foreground/80 [[data-triage-mirrored]_&]:[justify-content:flex-end] [[data-triage-mirrored]_&]:[text-align:right]">
        {/* Whose half it is - "For Harbour Supply" - and only that: who paid
            is the line above it. */}
        <Typography
          component="span"
          className="[font:inherit] inline align-baseline overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground/60 shrink-0"
        >
          {lead}
        </Typography>
        {isNameStaged ? (
          <Typography
            component="span"
            data-testid="triage-row-mirror-entity"
            className="[font:inherit] inline align-baseline overflow-hidden text-ellipsis whitespace-nowrap min-w-0"
          >
            <TriageStagedChange
              was={staged?.was?.name ?? null}
              becomes={staged?.becomes?.name ?? null}
            />
          </Typography>
        ) : (
          settlementName && (
            <TriageRowHeaderLink
              nodeId={settlementEntity.id}
              testId="triage-row-mirror-entity"
              keyboardActive={false}
            >
              {settlementName}
            </TriageRowHeaderLink>
          )
        )}
        {account ? (
          <>
            <Typography
              component="span"
              className="[font:inherit] inline align-baseline overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground/80 shrink-0"
            >
              /
            </Typography>
            <div className="min-w-0 overflow-hidden">
              <TriageRowHeaderLink
                nodeId={account.id}
                testId="triage-row-mirror-account"
                keyboardActive={false}
              >
                {account.name}
              </TriageRowHeaderLink>
            </div>
          </>
        ) : null}
      </div>
      {formattedDate ? (
        <Typography
          level="body-xs"
          className="col-2 text-muted-foreground/80 whitespace-nowrap text-[11px] [justify-self:end]"
        >
          {formattedDate}
        </Typography>
      ) : null}
      {description || staged ? (
        <Typography
          level="title-md"
          className="col-1 min-w-0 text-muted-foreground [[data-triage-mirrored]_&]:[justify-content:flex-end] [[data-triage-mirrored]_&]:[text-align:right]"
        >
          {staged ? (
            staged.was?.says === staged.becomes?.says ? (
              staged.becomes?.says
            ) : (
              <TriageStagedChange
                was={staged.was?.says ?? null}
                becomes={staged.becomes?.says ?? null}
              />
            )
          ) : descriptionNodeId ? (
            <TriageRowHeaderLink
              nodeId={descriptionNodeId}
              testId="triage-row-mirror-description"
              tone="emphasis"
            >
              {description}
            </TriageRowHeaderLink>
          ) : (
            description
          )}
        </Typography>
      ) : null}
      {formattedAmount ? (
        <Typography
          level="mono-body-md"
          // The weight of the payment's own amount above it.
          className="col-2 font-bold [font-variant-numeric:tabular-nums] whitespace-nowrap [justify-self:end] text-muted-foreground"
        >
          {formattedAmount}
        </Typography>
      ) : null}
    </div>
  );
}

export function TriageRowHeaderLink({
  children,
  nodeId,
  testId,
  keyboardActive = false,
  tone = "breadcrumb",
}: {
  children: ReactNode;
  nodeId?: string | null;
  testId?: string;
  /** When false, keep the control out of the list's Tab/Enter focus cycle. */
  keyboardActive?: boolean;
  /** ``breadcrumb``: triage row entity links. ``emphasis``: proposal settlement targets. ``title``: proposal card title row. */
  tone?: "breadcrumb" | "emphasis" | "title";
}) {
  const isRowPicked = useContext(TriageRowPickedContext);
  if (!nodeId) {
    return (
      <Typography
        component="span"
        data-testid={testId}
        className={cn(
          "[font:inherit] inline align-baseline overflow-hidden text-ellipsis whitespace-nowrap",
          tone === "title"
            ? "text-foreground"
            : tone === "emphasis"
              ? "text-muted-foreground"
              : "text-muted-foreground/80",
        )}
      >
        {children}
      </Typography>
    );
  }

  // On a payment not picked, the click is the row's: it picks the payment.
  // The name is the same button either way - drawn and cut short alike - but
  // there it takes no click of its own: one on a control is the control's,
  // never the row's (``SelectableCardList``), so the click goes through it.
  return (
    <Typography
      component="button"
      type="button"
      tabIndex={keyboardActive && isRowPicked ? 0 : -1}
      data-testid={testId}
      onMouseDown={
        isRowPicked
          ? (event) => {
              // Prevent focus steal + row selection clickthrough.
              event.preventDefault();
              event.stopPropagation();
            }
          : undefined
      }
      onClick={
        isRowPicked
          ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              const state = State.getState();
              // Same node already showing → close. Otherwise open (or switch to) it.
              // Store→URL sync owns the ``?panel=`` / ``nodeId`` query update.
              if (state.isNodePanelOpen && state.activePanelNodeId === nodeId) {
                state.setIsNodePanelOpen(false);
                return;
              }
              state.openNodePanel(nodeId, { autofocus: false });
            }
          : undefined
      }
      className={cn(
        "[font:inherit] inline align-baseline overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground/80 no-underline [text-decoration-thickness:initial] [text-decoration-style:initial] decoration-[initial] cursor-pointer [border:none] [background:none] p-0 max-w-full outline-[initial] [outline-style:none] [outline-width:initial] hover:text-muted-foreground hover:underline hover:[text-decoration-thickness:initial] hover:[text-decoration-style:initial] hover:decoration-[currentColor] hover:underline-offset-2 hover:outline-[initial] [&:hover]:[outline-style:none] hover:[outline-width:initial] focus:text-muted-foreground focus:underline focus:[text-decoration-thickness:initial] focus:[text-decoration-style:initial] focus:decoration-[currentColor] focus:underline-offset-2 focus:outline-[initial] [&:focus]:[outline-style:none] focus:[outline-width:initial] focus-visible:text-muted-foreground focus-visible:underline focus-visible:[text-decoration-thickness:initial] focus-visible:[text-decoration-style:initial] focus-visible:decoration-[currentColor] focus-visible:underline-offset-2 focus-visible:outline-[initial] [&:focus-visible]:[outline-style:none] focus-visible:[outline-width:initial]",
        tone === "title"
          ? "text-foreground"
          : tone === "emphasis"
            ? "text-muted-foreground"
            : "text-muted-foreground/80",
        tone === "title"
          ? "hover:text-foreground hover:underline hover:[text-decoration-thickness:initial] hover:[text-decoration-style:initial] hover:decoration-[currentColor] hover:underline-offset-2 hover:outline-[initial] [&:hover]:[outline-style:none] hover:[outline-width:initial] focus:text-foreground focus:underline focus:[text-decoration-thickness:initial] focus:[text-decoration-style:initial] focus:decoration-[currentColor] focus:underline-offset-2 focus:outline-[initial] [&:focus]:[outline-style:none] focus:[outline-width:initial] focus-visible:text-foreground focus-visible:underline focus-visible:[text-decoration-thickness:initial] focus-visible:[text-decoration-style:initial] focus-visible:decoration-[currentColor] focus-visible:underline-offset-2 focus-visible:outline-[initial] [&:focus-visible]:[outline-style:none] focus-visible:[outline-width:initial]"
          : tone === "emphasis"
            ? "hover:text-muted-foreground hover:underline hover:[text-decoration-thickness:initial] hover:[text-decoration-style:initial] hover:decoration-[currentColor] hover:underline-offset-2 hover:outline-[initial] [&:hover]:[outline-style:none] hover:[outline-width:initial] focus:text-muted-foreground focus:underline focus:[text-decoration-thickness:initial] focus:[text-decoration-style:initial] focus:decoration-[currentColor] focus:underline-offset-2 focus:outline-[initial] [&:focus]:[outline-style:none] focus:[outline-width:initial] focus-visible:text-muted-foreground focus-visible:underline focus-visible:[text-decoration-thickness:initial] focus-visible:[text-decoration-style:initial] focus-visible:decoration-[currentColor] focus-visible:underline-offset-2 focus-visible:outline-[initial] [&:focus-visible]:[outline-style:none] focus-visible:[outline-width:initial]"
            : "hover:text-muted-foreground/80 hover:underline hover:[text-decoration-thickness:initial] hover:[text-decoration-style:initial] hover:decoration-[currentColor] hover:underline-offset-2 hover:outline-[initial] [&:hover]:[outline-style:none] hover:[outline-width:initial] focus:text-muted-foreground/80 focus:underline focus:[text-decoration-thickness:initial] focus:[text-decoration-style:initial] focus:decoration-[currentColor] focus:underline-offset-2 focus:outline-[initial] [&:focus]:[outline-style:none] focus:[outline-width:initial] focus-visible:text-muted-foreground/80 focus-visible:underline focus-visible:[text-decoration-thickness:initial] focus-visible:[text-decoration-style:initial] focus-visible:decoration-[currentColor] focus-visible:underline-offset-2 focus-visible:outline-[initial] [&:focus-visible]:[outline-style:none] focus-visible:[outline-width:initial]",
        !isRowPicked && "pointer-events-none",
      )}
    >
      {children}
    </Typography>
  );
}

/** The colours of the landing page's rotating chip, as RGB: how the app says AI. */
const AI_GRADIENT_STOPS = ["68, 255, 0", "0, 98, 255", "152, 56, 255"] as const;

/** The AI's mark: a star in the colours the app says AI in. */
function TriageAiStar() {
  const gradientId = useId();
  return (
    <span
      data-testid="triage-proposal-ai-star"
      className="inline-flex shrink-0"
    >
      <svg width={0} height={0} style={{ position: "absolute" }} aria-hidden>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            {AI_GRADIENT_STOPS.map((color, index) => (
              <stop
                key={color}
                offset={`${(index / (AI_GRADIENT_STOPS.length - 1)) * 100}%`}
                stopColor={`rgb(${color})`}
              />
            ))}
          </linearGradient>
        </defs>
      </svg>
      <SparklesIcon size={12} style={{ fill: `url("#${gradientId}")` }} />
    </span>
  );
}

/**
 * A chip on a queue row's line of chips: an outline badge on the muted ground
 * (in dark, the secondary), at the middle corners, its words the muted ones
 * at the body's size. One that does something is its button (``asChild``),
 * hovered a step deeper.
 */
export const TRIAGE_ROW_CHIP_CLASS = [
  "h-7 px-2 text-sm rounded-md",
  "bg-muted dark:bg-secondary border-border text-muted-foreground",
  "[button&]:hover:bg-secondary/80 [button&]:hover:text-muted-foreground",
].join(" ");

/**
 * A chip at a card's foot: the size of the category chip among them - small
 * words, a little room above and below, round at the ends. Its buttons are
 * ``TRIAGE_ROW_FOOT_SIZE`` squares.
 */
export const TRIAGE_ROW_FOOT_CHIP_CLASS = cn(
  TRIAGE_ROW_CHIP_CLASS,
  "h-auto py-0.75 text-xs rounded-xl",
  // In the half under a row, a size down: the smaller words, less room, its
  // marks 10px.
  "[[data-triage-row-half]_&]:text-[11px] [[data-triage-row-half]_&]:py-0.25 [[data-triage-row-half]_&]:px-1.5 [[data-triage-row-half]_&]:[&_svg]:size-2.5",
);

/**
 * What a chip on a payment's row says of what it is about - each one way,
 * wherever a chip says it, and named on it (``data-chip-state``): on the books,
 * solid on the chip's ground (``set``); only on offer, dashed with no ground of
 * its own, its words quieter (``suggested``); wanted and neither, its mark alone
 * in red, dashed (``missing``); and what Book is about to make of it, edged as
 * one on the books, from what it is to what it becomes (``staged``).
 */
export const TRIAGE_ROW_CHIP_STATE_CLASS = {
  set: "border-solid",
  suggested:
    "bg-transparent dark:bg-transparent text-muted-foreground/80 border-dashed",
  // Its edge the other chips' line, dashed: the red is its words and mark.
  missing:
    "bg-transparent dark:bg-transparent text-destructive border-dashed border-border",
  staged: "bg-transparent dark:bg-transparent border-solid",
} as const;

/** A suggestion's line as triage reads it: what it posts to, in which business, and how much. */
export type SuggestionLine = {
  amount?: string | number | null;
  description?: string | null;
  entity?: {
    id?: string | null;
    identifier?: string | null;
    name?: string | null;
  } | null;
  chartedAccount?: {
    id?: string | null;
    identifier?: string | null;
    name?: string | null;
    fullPath?: string[] | null;
  } | null;
  /** A catalogue account the business has not made yet; accepting makes it. */
  catalogueAccountName?: string | null;
  /** Where in the chart accepting makes it, as an account's ``fullPath`` says. */
  catalogueAccountPath?: string[] | null;
};

/**
 * A payment as its suggestions' lines are read against it - the queue's row or
 * the payment open - its amount, its business, and its bank account.
 */
type TriageLinesPayment = {
  amount?: string | number | null;
  entity?: { identifier?: string | null } | null;
  financialAccount?: {
    chartedAccount?: { identifier?: string | null } | null;
  } | null;
};

/**
 * Where an account sits in the chart, as a card says it under the account's
 * own name: all it is under, the chart's root left off - "Expenses / Overhead"
 * - and nothing for one directly under the root. From its ``fullPath``, itself
 * last; without one - the queue's row asks for none - nothing.
 */
export function formatAccountPath(
  fullPath: readonly (string | null)[] | null | undefined,
): string {
  return (fullPath ?? [])
    .slice(1, -1)
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" / ");
}

/**
 * A suggestion's lines less the payment's own side: the bank account's line,
 * and the line in the payment's business mirroring the payment, whatever
 * account it names. Another business's line of the same amount is its own.
 */
export function offsetLines(
  suggestion: { lines?: readonly unknown[] | null },
  triage: TriageLinesPayment,
): SuggestionLine[] {
  const bankAccountId =
    triage.financialAccount?.chartedAccount?.identifier ?? null;
  const txnAmount = Number(triage.amount ?? 0);
  const home = triage.entity?.identifier;
  return ((suggestion.lines ?? []) as SuggestionLine[]).filter((line) => {
    const signed = Number(line.amount ?? 0);
    if (!Number.isFinite(signed) || signed === 0) return false;
    const accountId = line.chartedAccount?.identifier ?? null;
    if (
      accountId != null &&
      bankAccountId != null &&
      accountId === bankAccountId
    ) {
      return false;
    }
    const isHome = !line.entity?.identifier || line.entity.identifier === home;
    return !(isHome && signed === txnAmount);
  });
}

/** What owes or is owed between two businesses, by the accounts' names the server gives them. */
export const INTER_ENTITY_ACCOUNTS = new Set([
  "Inter-Entity Receivable",
  "Inter-Entity Payable",
]);

/**
 * The accounts a suggestion codes the payment to, each once, from its lines:
 * not the bank's, and not what one business owes another for it - that is the
 * "owe" of a suggestion for another business, said with it. Its card's title
 * and the queue's row name them alike.
 */
export function proposalSuggestedAccounts(
  suggestion: { lines?: readonly unknown[] | null },
  triage: TriageLinesPayment,
): Array<{
  name: string;
  /** Where it sits in the chart (``formatAccountPath``). */
  path: string;
  nodeId: string | null;
  /** From the catalogue, made when the suggestion is accepted. */
  isNew?: boolean;
}> {
  const accounts: Array<{
    name: string;
    path: string;
    nodeId: string | null;
    isNew?: boolean;
  }> = [];
  // Its lines less the payment's own side, whatever account that side names.
  for (const line of offsetLines(suggestion, triage)) {
    const account = line.chartedAccount;
    if (!account) {
      if (line.catalogueAccountName) {
        accounts.push({
          name: line.catalogueAccountName,
          path: formatAccountPath(line.catalogueAccountPath),
          nodeId: null,
          isNew: true,
        });
      }
      continue;
    }
    if (INTER_ENTITY_ACCOUNTS.has(account.name?.trim() ?? "")) continue;
    const name = account.name?.trim();
    if (name) {
      accounts.push({
        name,
        path: formatAccountPath(account.fullPath),
        nodeId: account.id ?? null,
      });
    }
  }
  const seen = new Set<string>();
  return accounts.filter(
    // Each once - two of one name are two where they sit apart.
    (account) =>
      !seen.has(`${account.path}/${account.name}`) &&
      seen.add(`${account.path}/${account.name}`),
  );
}

/** A document that came by email, as the email chip says it. */
type TriageEmailedDocument = {
  identifier: string;
  /** Its forwarder, or the sender in a connected mailbox. */
  fromEmailAddress?: string | null;
  /** The mailbox it was found in; else forwarded to the business. */
  externalProvider?: string | null;
  /** A draft in review in its chat while it has one. */
  pendingChange?: unknown;
  /** Whose it is: named where it is another business's than the payment's. */
  entity?: { identifier?: string | null; name?: string | null } | null;
};

/** The connected mailboxes a document is found in, by their provider. */
const MAILBOXES: Record<string, string> = {
  GMAIL: "Gmail",
  OUTLOOK: "Outlook",
};

/**
 * Documents that came by email: its envelope, who sent them - the first, and
 * how many more - and where they stand: in review while one waits in its chat,
 * confirmed once each is. On hover, how each came - forwarded to the business,
 * or found in a connected mailbox - and picked, the address copied. The same on
 * the queue's row, on a draft's card among a payment's suggestions, and on a
 * suggestion offering one. Whose they are, where another business's, is the
 * chip beside it (``TriageDocumentBusinessChip``), or on a suggestion the
 * control that moves it.
 */
export function TriageEmailChip({
  documents,
  className,
  ...props
}: {
  documents: TriageEmailedDocument[];
} & Omit<ComponentProps<typeof Badge>, "children">) {
  const senders = [
    ...new Set(
      documents.flatMap((document) => document.fromEmailAddress ?? []),
    ),
  ];
  const isDraft = documents.some((document) => document.pendingChange != null);
  // Where it stands, in the app's own colours for a status: the orange of one
  // in review, the green of one confirmed.
  const inReview = useEnumColor(EnumColors.ORANGE);
  const confirmed = useEnumColor(EnumColors.GREEN);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          asChild
          // Come in, not yet matched: on offer.
          data-chip-state="suggested"
          // A control of its own: picking it is not picking what it is on.
          onMouseDown={(event) => event.preventDefault()}
          {...props}
          className={cn(
            // A chip of a card's line, wherever it is drawn: as tall as the others.
            TRIAGE_ROW_FOOT_CHIP_CLASS,
            "text-muted-foreground",
            // An address runs long: it gives way, the marks after it do not.
            "min-w-0 max-w-full",
            "transition-[box-shadow] duration-120 ease-[ease] hover:shadow-sm",
            TRIAGE_ROW_CHIP_STATE_CLASS.suggested,
            className,
          )}
        >
          <button
            type="button"
            onClick={
              senders.length > 0
                ? (event) => {
                    event.stopPropagation();
                    void navigator.clipboard.writeText(senders[0]);
                    State.getState().setSnackbarMessage("Copied email address");
                  }
                : undefined
            }
          >
            {senders.length > 0 ? (
              <Icon
                ReactIcon={MailIcon}
                size="xs"
                data-testid="triage-email-envelope"
              />
            ) : undefined}
            {senders.length > 0 ? (
              <span data-testid="triage-email-sender">
                {senders[0]}
                {senders.length > 1
                  ? `, +${senders.length - 1} ${senders.length === 2 ? "other" : "others"}`
                  : null}
              </span>
            ) : null}
            <span className="flex items-center">
              <span
                className="flex text-(--triage-email-mark)"
                style={
                  {
                    "--triage-email-mark": isDraft
                      ? inReview.color
                      : confirmed.color,
                  } as CSSProperties
                }
              >
                {isDraft ? (
                  <Icon
                    ReactIcon={icons.inReview}
                    size="xs"
                    role="img"
                    aria-label="In review"
                    data-testid="in-review-icon"
                  />
                ) : (
                  <Icon
                    ReactIcon={icons.confirmed}
                    size="xs"
                    role="img"
                    aria-label="Confirmed"
                    data-testid="confirmed-icon"
                  />
                )}
              </span>
            </span>
          </button>
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top">
        <div>
          {documents.map((document) => {
            const mailbox = MAILBOXES[document.externalProvider ?? ""];
            // Whose it is, by name.
            const whose = document.entity?.name ?? "the business";
            return (
              <div key={document.identifier}>
                {!document.fromEmailAddress
                  ? `Drafted in a chat of ${whose}`
                  : mailbox
                    ? `Imported to ${whose} from ${document.fromEmailAddress}, a connected ${mailbox} mailbox`
                    : `Forwarded to ${whose} by ${document.fromEmailAddress}`}
                {document.pendingChange
                  ? " - a draft in review in its chat"
                  : " - confirmed"}
              </div>
            );
          })}
          {senders.length > 0 ? (
            <div className="opacity-[0.7]">Click to copy the address</div>
          ) : null}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/** The way money went between two businesses, before the other's name: out to it, or in from it. */
export function TriageBusinessArrow({ isOut }: { isOut: boolean }) {
  return (
    <Icon
      ReactIcon={isOut ? ArrowRightIcon : ArrowLeftIcon}
      size="xs"
      data-testid="triage-business-arrow"
    />
  );
}

/**
 * A chip on a card's line of chips - the one way one is drawn there, whatever
 * it says: in the state it is in, by its edge (``TRIAGE_ROW_CHIP_STATE_CLASS``),
 * a key after its words let into its padding so it is as tall as the others
 * on its line, and pressed, the keyboard kept where it was. Said in full
 * under the pointer, where *tooltip* is given.
 */
export function TriageChip({
  state,
  startDecorator,
  endDecorator,
  onClick,
  disabled,
  tooltip,
  testId,
  className,
  children,
  ...data
}: {
  state: keyof typeof TRIAGE_ROW_CHIP_STATE_CLASS;
  startDecorator?: ReactNode;
  endDecorator?: ReactNode;
  onClick?: (event: ReactMouseEvent) => void;
  disabled?: boolean;
  tooltip?: string;
  testId?: string;
  className?: string;
  children: ReactNode;
} & Record<`data-${string}`, string | undefined>) {
  const content = (
    <>
      {startDecorator}
      {children}
      {endDecorator != null && (
        <span className="ml-1.5 -my-px flex">{endDecorator}</span>
      )}
    </>
  );
  // A chip that does something is its button; one that does not, words.
  const chip = (
    <Badge
      variant="outline"
      asChild={onClick != null}
      {...data}
      data-testid={testId}
      data-chip-state={state}
      onMouseDown={onClick ? (event) => event.preventDefault() : undefined}
      className={cn(
        TRIAGE_ROW_FOOT_CHIP_CLASS,
        "min-w-0 max-w-full",
        TRIAGE_ROW_CHIP_STATE_CLASS[state],
        className,
      )}
    >
      {onClick ? (
        <button type="button" onClick={onClick} disabled={disabled}>
          {content}
        </button>
      ) : (
        content
      )}
    </Badge>
  );
  return tooltip ? (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent side="top">{tooltip}</TooltipContent>
    </Tooltip>
  ) : (
    chip
  );
}

/** A category's colour, a dot before its name - wherever a chip names one. */
export function TriageCategoryDot({
  color,
}: {
  color: string | null | undefined;
}) {
  const dot = useEnumColor(categoryEnumColor(color));
  return (
    <span
      aria-hidden
      data-enum-dot=""
      className="shrink-0 w-1.5 h-1.5 rounded-[50%] bg-(--triage-category-dot)"
      style={{ "--triage-category-dot": dot.borderColor } as CSSProperties}
    />
  );
}

/**
 * Another business, on a chip - the one way it is drawn wherever triage says
 * a payment or a document is, or would be, another's: the business's mark, how
 * the two settle it where that is said ("Equity"), the arrow the way the money
 * went, and the business. In the state it is in - on the books (``set``),
 * offered (``suggested``), about to change (``staged``) - and said in full
 * under the pointer.
 */
export function TriageBusinessChip({
  business,
  isOut,
  treatment,
  state,
  tooltip,
  testId,
  endDecorator,
  onClick,
  disabled,
  className,
}: {
  business: ReactNode;
  isOut: boolean;
  /** How the two settle it, named before the arrow - the one name for it, wherever it is said. */
  treatment?: SettlementTreatmentEnum | null;
  state: "set" | "suggested" | "staged";
  tooltip?: string;
  testId?: string;
  endDecorator?: ReactNode;
  onClick?: (event: ReactMouseEvent) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <TriageChip
      testId={testId}
      state={state}
      disabled={disabled}
      tooltip={tooltip}
      startDecorator={
        <Icon ReactIcon={iconsByTypename.EntityObject} size="xs" />
      }
      endDecorator={endDecorator}
      onClick={onClick}
      className={className}
    >
      <span
        data-testid={testId ? `${testId}-name` : undefined}
        className="inline-flex items-center gap-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {treatment ? (
          <span>
            {
              TRIAGE_SETTLEMENTS.find((row) => row.treatment === treatment)
                ?.title
            }
          </span>
        ) : null}
        <TriageBusinessArrow isOut={isOut} />
        <span className="overflow-hidden text-ellipsis">{business}</span>
      </span>
    </TriageChip>
  );
}

/**
 * The other business documents are for, beside the chip that says who emailed
 * them: after an arrow the way the money went - to it when lent, from it when
 * it came back - and on hover, which business paid for which. Nothing where
 * they are the payment's own business's.
 */
export function TriageDocumentBusinessChip({
  documents,
  business,
  isOut = true,
  className,
}: {
  documents: TriageEmailedDocument[];
  /** The payment's business: a document of another is said to be for that one, paid by this. */
  business: { identifier?: string | null; name?: string | null } | null;
  /** Paid out - lent to the business it was for - or taken in. */
  isOut?: boolean;
  className?: string;
}) {
  // The other businesses they are for, each once.
  const others = [
    ...new Set(
      documents.flatMap((document) =>
        document.entity?.identifier &&
        business?.identifier &&
        document.entity.identifier !== business.identifier
          ? [document.entity.name ?? ""]
          : [],
      ),
    ),
  ];
  if (others.length === 0) return null;
  return (
    <TriageBusinessChip
      testId="triage-document-business"
      state="suggested"
      isOut={isOut}
      business={`${others[0]}${
        others.length > 1
          ? `, +${others.length - 1} ${others.length === 2 ? "other" : "others"}`
          : ""
      }`}
      tooltip={
        business?.name
          ? isOut
            ? `Paid by ${business.name} for ${others.join(", ")}`
            : `Received by ${business.name} from ${others.join(", ")}`
          : isOut
            ? `Paid for ${others.join(", ")}`
            : `Received from ${others.join(", ")}`
      }
      className={className}
    />
  );
}

/**
 * What a payment's suggestions come to, on its row, in two chips at most: its
 * paperwork - who emailed its document in, or how many documents are suggested
 * for it, or in red that none is, a button that offers to search the mailboxes
 * for one - and how it would be booked: the one way, named, or how many ways,
 * each kind's mark once and every one on hover. A document match is its own
 * booking: the document's lines code it. Another business it is for, and the
 * AI's star, ride on the booking. The queue can then be read for what each row
 * will take without opening it.
 */
export function categoryEnumColor(
  color: string | null | undefined,
): (typeof EnumColors)[keyof typeof EnumColors] {
  if (color && color in TagColors) {
    return TagColors[color as TagColorEnum];
  }
  return EnumColors.GREY_1;
}

/** Another business a payment is for, and how the two settle it - unsaid, ``null``. */
export type TriageRowBusiness = {
  id: string;
  identifier: string;
  /** Its name, where it has one: none, none is drawn. */
  name: string | null;
  treatment: SettlementTreatmentEnum | null;
};

/**
 * A change staged - what Book is about to make of it: what it is, struck out
 * on red, then what it becomes, on green; either alone where there is none.
 * The same on a chip and on a payment's half.
 */
export function TriageStagedChange({
  was,
  becomes,
}: {
  was: ReactNode;
  becomes: ReactNode;
}) {
  const red = useEnumColor(EnumColors.RED);
  const green = useEnumColor(EnumColors.GREEN);
  return (
    <>
      {was ? (
        <span
          data-staged="was"
          className="text-(--staged-color) bg-(--staged-background) rounded-sm pl-0.5 pr-0.5 line-through"
          style={
            {
              "--staged-color": red.color,
              "--staged-background": red.backgroundColor,
            } as CSSProperties
          }
        >
          {was}
        </span>
      ) : null}
      {was && becomes ? " → " : null}
      {becomes ? (
        <span
          data-staged="becomes"
          className="text-(--staged-color) bg-(--staged-background) rounded-sm pl-0.5 pr-0.5"
          style={
            {
              "--staged-color": green.color,
              "--staged-background": green.backgroundColor,
            } as CSSProperties
          }
        >
          {becomes}
        </span>
      ) : null}
    </>
  );
}

/**
 * What a payment becomes, listed beside one of its payee's about to be booked:
 * booked like it (``like``), from what it is booked to now (``now``), where it
 * is booked - or, ``like`` unset, left as it is.
 */
export type TriageRowBecomes = {
  now: string | null;
  like: string | null;
  /**
   * The other business it is for - and how the two settle it - now and as the
   * change picked leaves it: none, its own. Changed, its half is staged.
   */
  business?: { now: TriageRowBusiness | null; like: TriageRowBusiness | null };
  /**
   * Matched each to its own document: the one it would pay - a record, or a
   * chat's draft - what the mailboxes hold for it to import first, or why
   * nothing is done to it.
   */
  document?: {
    label: string;
  } | null;
};

function TriageRowSuggestionChips({
  item,
  becomes = null,
}: {
  item: TriageListItemFragment;
  becomes?: TriageRowBecomes | null;
}) {
  const { findDocumentFor } = useTriageViewActions();
  // Its documents and the business it is booked in, unless the person has
  // hidden them from the queue's eye.
  const hidden = State((state) => state.hiddenTriageRowParts);
  const isDocumentsShown = !hidden.includes("documents");
  const isBusinessesShown = !hidden.includes("businesses");
  // The category it is filed under: its colour a dot before its words, on
  // its chip as it is and as a change to it is staged.
  const categoryDot = <TriageCategoryDot color={item.categoryObj?.color} />;
  const pending = (item.suggestions ?? []).filter(
    (suggestion) => suggestion.status === TriageSuggestionStatusEnum.Pending,
  );
  type Way = (typeof pending)[number];
  // Each way it could be booked, once: rules that come to the same are one.
  const conclusion = (suggestion: Way) =>
    JSON.stringify([
      suggestion.job,
      (suggestion.lines ?? [])
        .map((line) =>
          [
            line.entity?.identifier ?? "",
            line.chartedAccount?.identifier ?? line.catalogueAccountName ?? "",
            Number(line.amount ?? 0).toFixed(2),
          ].join(":"),
        )
        .sort(),
    ]);
  const ways = pending.filter(
    (suggestion, index) =>
      pending.findIndex(
        (other) => conclusion(other) === conclusion(suggestion),
      ) === index,
  );
  const isAi = (suggestion: Way) =>
    ["ai", "agent"].includes(suggestion.source?.trim().toLowerCase() ?? "");
  // Paid out: lent to the business it was for. Taken in: back from it.
  const isOut = Number(item.amount ?? 0) < 0;
  // The documents it pays - its own row's, booked, and its row's in the
  // business it is for, booked or waiting (a move books its cost there at
  // once) - not what it is offered: nothing is.
  const paidThere = item.movedToTransaction?.paymentFor ?? [];
  const paid = item.isBooked
    ? [...(item.paymentFor ?? []), ...paidThere]
    : paidThere.length > 0
      ? paidThere
      : null;
  const documentCount =
    paid?.length ??
    ways.filter((suggestion) => suggestion.job === "MATCH_DOCUMENT").length;
  // The first document offered - or, booked, paid: its name and whose it is
  // - its logo - lead the document chip, the rest counted after it.
  const offeredDocument =
    ways
      .filter((suggestion) => suggestion.job === "MATCH_DOCUMENT")
      .flatMap((suggestion) => suggestion.ruleSuggestionDocumentOptions ?? [])
      .find((option) => option.document)?.document ?? null;
  const primaryDocument = paid ? (paid[0] ?? null) : offeredDocument;
  const documentOrganization = paid
    ? null
    : (offeredDocument?.contact?.organization ?? null);
  // The ways to book it besides a document: a document match is its
  // paperwork's to say.
  const bookings = ways.filter(
    (suggestion) => suggestion.job !== "MATCH_DOCUMENT",
  );
  // A transfer, or a duplicate, has no receipt to find: its way is its own.
  // Any other is a way round the paperwork it wants.
  const expectsDocument =
    ways.length === 0 ||
    ways.some(
      (suggestion) =>
        !["TRANSFER", "DUPLICATE", "PAIR"].includes(suggestion.job ?? ""),
    );
  // The documents for it that came by email - forwarded to its business, or
  // found in a mailbox searched for it - those still in review in a chat among
  // them, and one confirmed there until the suggestion it makes is in.
  const emailed = item.emailedDocuments ?? [];
  // A way that posts it in another business than its own - or, moved, than
  // the one it is for, which its half names.
  const isElsewhere = (suggestion: Way) =>
    (suggestion.lines ?? []).some(
      (line) =>
        line.entity?.identifier &&
        line.entity.identifier !== item.entity?.identifier &&
        line.entity.identifier !== item.belongsToEntity?.identifier,
    );
  // The other businesses those ways post in, each once.
  const others = [
    ...new Map(
      bookings
        .filter(isElsewhere)
        .flatMap((suggestion) =>
          (suggestion.lines ?? []).flatMap((line) =>
            line.entity?.identifier &&
            line.entity.identifier !== item.entity?.identifier &&
            line.entity.identifier !== item.belongsToEntity?.identifier
              ? [[line.entity.identifier, line.entity.name ?? ""] as const]
              : [],
          ),
        ),
    ).values(),
  ];
  const arrow = <TriageBusinessArrow isOut={isOut} />;

  return (
    <>
      {/* First, its paperwork: who emailed its document in, or the
          documents it pays or is offered, or that it has none - one chip, in
          the state its paperwork is in. */}
      {!isDocumentsShown ? null : becomes?.document ? (
        // Its own document, as the payment open's payee's others are each
        // offered theirs - or what the mailboxes hold for it: on offer.
        <TriageChip
          testId="triage-row-becomes-document"
          state="suggested"
          startDecorator={
            <Icon ReactIcon={iconsByTypename.DocumentObject} size="xs" />
          }
        >
          {becomes.document.label}
        </TriageChip>
      ) : emailed.length > 0 ? (
        <>
          <TriageEmailChip
            documents={emailed}
            data-testid="triage-row-email-chip"
            className="min-w-0 max-w-full"
          />
          <TriageDocumentBusinessChip
            documents={emailed}
            business={item.entity}
            isOut={isOut}
            className={cn(TRIAGE_ROW_FOOT_CHIP_CLASS, "min-w-0 max-w-full")}
          />
        </>
      ) : documentCount > 0 ? (
        // The documents it pays - on the books - or is offered: the first
        // named, whose it is by its logo, the rest counted after it.
        <Badge
          variant="outline"
          data-testid="triage-row-document-chip"
          data-chip-state={paid ? "set" : "suggested"}
          // The size of the category chip beside it.
          className={cn(
            TRIAGE_ROW_FOOT_CHIP_CLASS,
            "text-muted-foreground/80",
            paid
              ? // On the category chip's ground.
                [TRIAGE_ROW_CHIP_STATE_CLASS.set, "bg-background"]
              : [
                  TRIAGE_ROW_CHIP_STATE_CLASS.suggested,
                  // Only on offer: whose it is, in grey.
                  "[&_[data-testid='triage-row-document-logo']]:[filter:grayscale(1)]",
                ],
          )}
        >
          {documentOrganization ? (
            <BoxForOrganization
              organization={documentOrganization}
              size={14}
              fallbackIcon={iconsByTypename.DocumentObject}
              data-testid="triage-row-document-logo"
            />
          ) : (
            <Icon ReactIcon={iconsByTypename.DocumentObject} size="xs" />
          )}
          {primaryDocument?.name ? (
            <span className="inline-flex items-baseline gap-1">
              <span
                data-testid="triage-row-document-name"
                className={cn(
                  paid ? "text-foreground" : "text-muted-foreground",
                  "max-w-40 overflow-hidden text-ellipsis whitespace-nowrap",
                )}
              >
                {primaryDocument.name}
              </span>
              {documentCount > 1 ? `+${documentCount - 1}` : null}
            </span>
          ) : (
            <>
              <span
                className={cn(
                  paid ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {documentCount}
              </span>{" "}
              {documentCount === 1 ? "document" : "documents"}
            </>
          )}
        </Badge>
      ) : expectsDocument ? (
        // Or what is not: a payment with no document - none paid, offered or
        // emailed in for it - where it would have one. Its mark alone, in red,
        // dashed, the words on hover - a button, which opens the payment where
        // its mailboxes are searched for one, or a document is chosen.
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              asChild
              aria-label="No documents"
              data-testid="triage-row-missing-document-chip"
              data-chip-state="missing"
              // A control of the row's own: picking it is not only picking
              // the row.
              onMouseDown={(event) => event.preventDefault()}
              // A chip as the others are - their height, edge and corners -
              // its mark alone in it.
              className={cn(
                TRIAGE_ROW_FOOT_CHIP_CLASS,
                TRIAGE_ROW_CHIP_STATE_CLASS.missing,
              )}
            >
              <button type="button" onClick={() => findDocumentFor?.(item.id)}>
                {/* A document with the mark on it: what is missing, not only
                    that something is. */}
                {/* None, counted as the ways to book it are: the count, then
                    the mark. */}
                0
                <Icon ReactIcon={FileWarningIcon} size="xs" />
              </button>
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top">No documents</TooltipContent>
        </Tooltip>
      ) : null}
      {becomes?.like != null && becomes.like !== becomes.now ? (
        // Staged: booking it like the payment open files it from what it is
        // under now - or not booked - to what it becomes.
        <TriageChip
          testId="triage-row-becomes"
          state="staged"
          startDecorator={categoryDot}
        >
          <TriageStagedChange
            was={becomes.now ?? "Not booked"}
            becomes={becomes.like}
          />
        </TriageChip>
      ) : item.categoryObj ? (
        // On the books: the category it is filed under, its colour a dot
        // before its name.
        <TriageChip
          testId="triage-row-category"
          state="set"
          startDecorator={categoryDot}
          className="text-foreground"
        >
          {item.categoryObj.name}
        </TriageChip>
      ) : bookings.length > 0 ? (
        // Only on offer: every way to book it in one dashed chip, how many of
        // each kind by its mark - the AI's star where one is its - then how
        // many of them post it in another business, and which, behind the
        // arrow the money went; on hover, the same in words.
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              data-testid="triage-row-booking-chip"
              data-chip-state="suggested"
              className={cn(
                TRIAGE_ROW_FOOT_CHIP_CLASS,
                "min-w-0 max-w-full",
                TRIAGE_ROW_CHIP_STATE_CLASS.suggested,
              )}
            >
              <span
                data-testid="triage-row-booking-label"
                className="inline-flex items-center gap-1"
              >
                {[...new Set(bookings.map((suggestion) => suggestion.job))].map(
                  (job, index) => {
                    const of = bookings.filter(
                      (suggestion) => suggestion.job === job,
                    );
                    return (
                      <Fragment key={job}>
                        {index > 0 ? " · " : null}
                        <span
                          data-testid="triage-row-booking-way"
                          className="inline-flex items-center gap-1"
                        >
                          {of.length}
                          <span
                            data-testid="triage-row-booking-mark"
                            className="flex"
                          >
                            {of.some(isAi) ? (
                              <TriageAiStar />
                            ) : (
                              <Icon
                                ReactIcon={
                                  JOB_ICONS[job as TriageJobEnum] ??
                                  iconsByTypename.DocumentObject
                                }
                                size="xs"
                              />
                            )}
                          </span>
                        </span>
                      </Fragment>
                    );
                  },
                )}
              </span>
              {isBusinessesShown && others.length > 0 ? (
                <span className="flex items-center gap-1 min-w-0">
                  {" · "}
                  <span data-testid="triage-row-booking-elsewhere">
                    {bookings.filter(isElsewhere).length}
                  </span>
                  {/* Before the business, pointing the way the money went. */}
                  {arrow}
                  <span
                    data-testid="triage-row-booking-business"
                    className="overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {others[0]}
                    {others.length > 1
                      ? `, +${others.length - 1} ${others.length === 2 ? "other" : "others"}`
                      : null}
                  </span>
                </span>
              ) : undefined}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top">
            <div data-testid="triage-row-booking-hover">
              {[...new Set(bookings.map((suggestion) => suggestion.job))].map(
                (job) => {
                  const of = bookings.filter(
                    (suggestion) => suggestion.job === job,
                  );
                  const there = of.filter(isElsewhere).length;
                  return (
                    <div key={job}>
                      {`${of.length} ${
                        TRIAGE_RECOMMENDATION_NOUNS[job as TriageJobEnum]
                      } recommendation${of.length === 1 ? "" : "s"}${
                        there > 0
                          ? `, ${there} of which suggest${there === 1 ? "s" : ""} posting in another entity`
                          : ""
                      }`}
                    </div>
                  );
                },
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      ) : null}
      {/* Last: the AI working out more for it, whoever asked it to - the line
          says so until they are in. */}
      {item.isProcessing ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Spinner data-testid="triage-row-processing" />
          </TooltipTrigger>
          <TooltipContent side="bottom">
            The AI is working out suggestions
          </TooltipContent>
        </Tooltip>
      ) : null}
    </>
  );
}

/** One part of a card's line over its title: whose, or where - a link to it. */
type TriageCardCrumb = {
  nodeId?: string | null;
  label: ReactNode;
  testId?: string;
};

/**
 * A card of triage's, as the queue draws a payment and the suggestions draw
 * what is offered it, so the two read across: whose and where over its title
 * and its date at that line's end; its title, and its amount at that line's
 * end; under them a line of chips; and under all, a half of it elsewhere.
 * Each is set the one way wherever it is drawn - every card's lines at the
 * same heights - and a *marker* is centred on the card's own edge, level with
 * its title: a link's ball, where a line to the other side comes to. One drawn
 * inside another card (*isNested*) has its title and amount a size down, as a
 * half's are.
 */
export function TriageCard({
  crumbs,
  date,
  title,
  titleAfter,
  amount,
  tools,
  half,
  marker,
  markerEnd,
  isLoading = false,
  isNested = false,
  keyboardActive = false,
  testIds = {},
}: {
  crumbs: TriageCardCrumb[];
  date?: ReactNode;
  title: ReactNode;
  /** After its title on its line, past where it is cut short: a count, a chevron, a chip. */
  titleAfter?: ReactNode;
  amount?: ReactNode;
  /** Its line of chips under its title - none, no line. */
  tools?: ReactNode;
  /** Under it all: its half in another business, what it would do there. */
  half?: ReactNode;
  /** Centred on its left edge, level with its title. */
  marker?: ReactNode;
  /** Centred on its right edge, level with its title. */
  markerEnd?: ReactNode;
  isLoading?: boolean;
  /** Inside another card - a find on the Match card: its title and amount a size down. */
  isNested?: boolean;
  /** When false, its links are kept out of the list's Tab/Enter cycle. */
  keyboardActive?: boolean;
  /** What its parts are found by, where the queue's names are not theirs. */
  testIds?: {
    crumbs?: string;
    date?: string;
    title?: string;
    amount?: string;
    tools?: string;
  };
}) {
  // How far out its edge is from its words: the list's inset, and its line.
  const { size } = useSelectableListStyle();
  const inset = getSelectableRowInset(size, "cards");
  return (
    <>
      <div className={cn(TRIAGE_RECONCILE_ROW_GRID_CLASS, "relative")}>
        <div
          data-testid={testIds.crumbs}
          // As tall as when a pill for the business it was for sat on it:
          // the title keeps its distance under it, as in the lists picked
          // from by hand, and the row its height.
          className="flex items-center min-w-0 gap-1 font-medium text-xs leading-snug text-foreground pt-0.5 pb-0.5 [[data-triage-mirrored]_&]:[justify-content:flex-end] [[data-triage-mirrored]_&]:[text-align:right]"
        >
          <div className="flex items-center min-w-0 overflow-hidden shrink gap-1">
            {crumbs.map((crumb, index) => (
              <Fragment key={index}>
                {index > 0 ? (
                  <Typography
                    component="span"
                    className="[font:inherit] inline align-baseline overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground/80 shrink-0"
                  >
                    /
                  </Typography>
                ) : null}
                <div className={cn(index > 0 && "min-w-0 overflow-hidden")}>
                  <TriageRowHeaderLink
                    nodeId={crumb.nodeId}
                    testId={crumb.testId}
                    keyboardActive={keyboardActive}
                  >
                    <Skeleton loading={isLoading} variant="inline">
                      {crumb.label}
                    </Skeleton>
                  </TriageRowHeaderLink>
                </div>
              </Fragment>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end [justify-self:end] gap-1.5 shrink-0">
          <Typography
            level="body-sm"
            data-testid={testIds.date ?? "triage-row-date"}
            className="text-muted-foreground/80 whitespace-nowrap text-xs [font-weight:500]"
          >
            <Skeleton loading={isLoading} variant="inline">
              {date}
            </Skeleton>
          </Typography>
        </div>
        <Typography
          level={isNested ? "title-md" : "title-lg"}
          // The row's title, underlined as the row is hovered - not one inside
          // another card's, which is not the row's.
          data-selectable-list-title={isNested ? undefined : true}
          data-testid={testIds.title}
          className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap [[data-triage-mirrored]_&]:[justify-content:flex-end] [[data-triage-mirrored]_&]:[text-align:right]"
        >
          <Skeleton loading={isLoading} variant="inline">
            {title}
          </Skeleton>
          {titleAfter}
        </Typography>
        <Typography
          level={isNested ? "mono-body-md" : "mono-body-lg"}
          data-testid={testIds.amount ?? "triage-row-amount"}
          // Bold, as an amount beside a title is in the lists.
          className="font-bold [font-variant-numeric:tabular-nums] whitespace-nowrap [justify-self:end]"
        >
          <Skeleton loading={isLoading} variant="inline">
            {amount ?? "\u200b"}
          </Skeleton>
        </Typography>
        {[
          { side: "start", placed: marker },
          { side: "end", placed: markerEnd },
        ].map(({ side, placed }) =>
          placed ? (
            // Over its title's line, the whole of it, out to the card's edges:
            // what is in it centred on that line, and on the card's left - or
            // its right - edge, which mirrored or not stays where it is: it is
            // what a line to the other side comes to.
            <div
              key={side}
              // Both ends named: an absolute item's open end runs to the
              // card's own edge, not its line's.
              className={cn(
                "absolute [grid-column:1/-1] [grid-row:2/3] left-(--triage-marker-out) right-(--triage-marker-out) top-0 bottom-0 [direction:ltr] flex items-center pointer-events-none [&>*]:pointer-events-auto",
                side === "start" ? "justify-start" : "justify-end",
              )}
              style={
                {
                  "--triage-marker-out": `calc(-${`${inset.px * 8}px`} - 1px - ${TRIAGE_LINK_BALL / 2}px)`,
                } as CSSProperties
              }
            >
              {placed}
            </div>
          ) : null,
        )}
        {tools ? (
          <TriageCardTools testId={testIds.tools} isNested={isNested}>
            {tools}
          </TriageCardTools>
        ) : null}
      </div>
      {half}
    </>
  );
}

/** A payment, as far as where it was moved and how it settles there go. */
export type TriageMovedPayment = {
  amount?: string | number | null;
  belongsToEntity?: { name?: string | null } | null;
  settlementTreatment?: SettlementTreatmentEnum | null;
};

/**
 * A payment moved to another business, said - the one chip for it, wherever
 * it is said: the queue's row open or not, its suggestion's card, Similar's
 * line-up. Read from the payment itself - where it went and how the two
 * settle it - so it says one thing everywhere: "Equity → Helio SaaS", on the
 * books, solid. The open payment's has its Move's key and opens it.
 */
export function TriageMovedChip({
  payment,
  testId,
  endDecorator,
  onClick,
  disabled,
}: {
  payment: TriageMovedPayment;
  testId?: string;
  endDecorator?: ReactNode;
  onClick?: (event: ReactMouseEvent) => void;
  disabled?: boolean;
}) {
  const business = payment.belongsToEntity?.name;
  if (!business) return null;
  const treatment = TRIAGE_SETTLEMENTS.find(
    (row) => row.treatment === payment.settlementTreatment,
  )?.title;
  return (
    <TriageBusinessChip
      testId={testId}
      state="set"
      isOut={Number(payment.amount ?? 0) < 0}
      treatment={payment.settlementTreatment}
      business={business}
      tooltip={`Moved to ${business}${treatment ? ` as ${treatment.toLowerCase()}` : ""}`}
      endDecorator={endDecorator}
      onClick={onClick}
      disabled={disabled}
    />
  );
}

/** A link's ball across, in px: where a line between two cards starts and ends. */
export const TRIAGE_LINK_BALL = 10;
/**
 * How long a link's ball takes to fade in or out: out under anything marked
 * ``data-balls-hidden``, in again as the mark is taken off - by its style
 * alone, so no card is drawn again for it.
 */
export const TRIAGE_LINK_BALL_FADE_MS = 180;

/** One end of a link on a card (``TriageLinkBall``): on its left edge the end a line comes to, on its right the one it leaves from. */
export type TriageRowLink = {
  link: string;
  end: "from" | "to";
  isEngaged: boolean;
  /** Pressed, it engages the link, or lets it go - none, it is engaged for good. */
  onPress?: () => void;
  label?: string;
  testId: string;
};

/**
 * One end of a line drawn between two cards across the page (``TriageLinks``):
 * a ball on a card's edge - or beside it - the line drawn between the two of
 * a *link* while it is engaged. Engaged, solid; let go, the end that lets it
 * go is hollow and the other not drawn. Pressed, it does what it is for -
 * engages it, or lets it go.
 */
export function TriageLinkBall({
  link,
  end,
  isEngaged,
  onPress,
  label,
  testId,
  className,
  ...data
}: {
  link: string;
  /** The end the line leaves from, or comes to. */
  end: "from" | "to";
  isEngaged: boolean;
  onPress?: () => void;
  label?: string;
  testId?: string;
  className?: string;
} & Record<`data-${string}`, string | undefined>) {
  const Element = onPress ? "button" : "span";
  return (
    <Element
      {...data}
      type={onPress ? "button" : undefined}
      role={onPress ? "checkbox" : undefined}
      aria-checked={onPress ? isEngaged : undefined}
      aria-label={label}
      data-testid={testId}
      {...{ [`data-link-${end}`]: link }}
      data-link-engaged={isEngaged}
      onMouseDown={(event: ReactMouseEvent) => event.preventDefault()}
      onClick={
        onPress
          ? (event: ReactMouseEvent) => {
              event.stopPropagation();
              onPress();
            }
          : undefined
      }
      className={cn(
        "shrink-0 p-0 m-0 rounded-[50%] box-border [border-width:1.5px] [border-style:solid] border-primary",
        isEngaged ? "bg-primary" : "bg-transparent",
        onPress && "cursor-pointer",
        // Let go, the end it comes to is not there: the other, hollow,
        // engages it again.
        isEngaged || end === "from" ? "opacity-100" : "opacity-0",
        // The end a line leaves from fades in and out with the list it is in
        // (Similar's, by ``data-balls-hidden``).
        end === "from" && "[[data-balls-hidden]_&]:opacity-0",
        onPress && (isEngaged ? "hover:bg-primary/90" : "hover:bg-accent"),
        className,
      )}
      style={{
        width: TRIAGE_LINK_BALL,
        height: TRIAGE_LINK_BALL,
        ...(end === "from" && {
          transition: `opacity ${TRIAGE_LINK_BALL_FADE_MS}ms ease`,
        }),
      }}
    />
  );
}

/** A document as a card of triage's draws it, from whichever list it is read. */
export type TriageCardDocument = {
  name: string;
  reference: string | null;
  date: string | null;
  amount: number | null;
  /** Its amounts' currency: said beside them where it is not its business's. */
  currency: string | null;
  /** In another currency than the payment's: what the payment pays of it, said after its own. */
  amountPaid?: number | null;
  /** A draft in review, or an email in the mailboxes, is said so over its title. */
  kind?: "document" | "draft" | "email";
  /** Where it stands, over its title in its kind's place: the chat a draft is in review in. */
  status?: ReactNode;
  business: {
    id?: string | null;
    identifier?: string | null;
    name?: string | null;
  } | null;
  /** Who it is from: what goes with it where it is moved. */
  contact?: {
    emailAddress?: string | null;
    organization?: { name?: string | null } | null;
  } | null;
};

/**
 * A document a payment would be matched to, as a card - the one way it is
 * drawn, offered as a suggestion or lined up in Similar: its business over
 * "Match “its name”", its date and amount at their lines' ends, its own chip,
 * Space to look at it, and how it is settled in another business; and under
 * all, moved there with it where it is another business's, it and its
 * contact, staged once picked.
 */
export function TriageDocumentCard({
  document: shown,
  businessIdentifier,
  onView,
  viewHotkey = "space",
  businessChip,
  movesTo = null,
  isStaged = false,
  titleAfter,
  toolsAfter,
  marker,
  isLoading = false,
  testIds,
}: {
  document: TriageCardDocument;
  /** The business of the payment it would be matched to: an amount in its currency is said bare. */
  businessIdentifier: string | null | undefined;
  onView?: () => void;
  viewHotkey?: string;
  /** How it is settled in another business: on its line of chips after its own. */
  businessChip?: ReactNode;
  /** The business it goes to with the payment, where that is another than its own. */
  movesTo?: { id: string | null; name: string } | null;
  /** Picked: what it would do there is a change, staged. */
  isStaged?: boolean;
  titleAfter?: ReactNode;
  toolsAfter?: ReactNode;
  marker?: ReactNode;
  isLoading?: boolean;
  testIds?: Parameters<typeof TriageCard>[0]["testIds"];
}) {
  // A payment is in its business's currency: an amount in that one is said
  // bare, one in another with its own sign.
  const businessCurrency = State((state) =>
    state.entities.find((entity) => entity.identifier === businessIdentifier),
  )?.currency;
  const isSameCurrency = !shown.currency || shown.currency === businessCurrency;
  const date = shown.date ? Utils.fDate(new Date(shown.date)) : null;
  const amount =
    shown.amount == null
      ? null
      : [
          Utils.fCurrency(
            shown.amount,
            false,
            true,
            isSameCurrency ? undefined : (shown.currency ?? undefined),
          ),
          ...(shown.amountPaid != null
            ? [Utils.fCurrency(shown.amountPaid, false, true)]
            : []),
        ].join(" → ");
  const movesSays = `Move “${shown.name}”${
    shown.contact ? " and its contact" : ""
  } into it`;
  return (
    <TriageCard
      isLoading={isLoading}
      marker={marker}
      crumbs={[
        ...(shown.business?.name
          ? [{ nodeId: shown.business.id, label: shown.business.name }]
          : []),
        ...(shown.status
          ? [{ label: shown.status }]
          : shown.kind === "draft"
            ? [{ label: "Draft" }]
            : shown.kind === "email"
              ? [{ label: "Email" }]
              : []),
      ]}
      date={date}
      amount={amount ?? undefined}
      title={`Match “${shown.name}”`}
      titleAfter={titleAfter}
      tools={
        onView || businessChip || toolsAfter ? (
          <>
            {onView ? (
              // It, looked at by its own chip - its number, else its name:
              // the document itself, a fact of the card, not on offer - solid.
              <TriageChip
                testId="triage-proposal-view-chip"
                state="set"
                startDecorator={
                  <Icon ReactIcon={iconsByTypename.DocumentObject} size="xs" />
                }
                endDecorator={
                  <HotkeyDecorator
                    variant="outlined"
                    hotkey={viewHotkey}
                    color="inherit"
                  />
                }
                onClick={(event) => {
                  event.stopPropagation();
                  onView();
                }}
              >
                {shown.reference ? `#${shown.reference}` : shown.name}
              </TriageChip>
            ) : null}
            {businessChip}
            {toolsAfter}
          </>
        ) : null
      }
      half={
        movesTo ? (
          <div data-testid="triage-proposal-moves-document">
            <TriageReconcileRowMirror
              settlementEntity={{ id: movesTo.id, name: movesTo.name }}
              description={movesSays}
              // Picked, a change it would make there: staged, as a change
              // picked on a payment's row is.
              staged={
                isStaged
                  ? {
                      was: null,
                      becomes: { name: movesTo.name, says: movesSays },
                    }
                  : null
              }
            >
              {/* What goes there with it - it, its contact, the contact's
                  organization - each a chip, staged once it is picked. */}
              <TriageCardTools>
                {[
                  {
                    moves: "document",
                    icon: iconsByTypename.DocumentObject,
                    label: shown.name,
                  },
                  {
                    moves: "contact",
                    icon: iconsByTypename.ContactObject,
                    label: shown.contact?.emailAddress ?? null,
                  },
                  {
                    moves: "organization",
                    icon: iconsByTypename.OrganizationObject,
                    label: shown.contact?.organization?.name ?? null,
                  },
                ].map(({ moves, icon, label }) =>
                  label ? (
                    <TriageChip
                      key={moves}
                      testId="triage-proposal-moves-chip"
                      data-moves={moves}
                      state={isStaged ? "staged" : "suggested"}
                      startDecorator={<Icon ReactIcon={icon} size="xs" />}
                    >
                      {isStaged ? (
                        <TriageStagedChange was={null} becomes={label} />
                      ) : (
                        label
                      )}
                    </TriageChip>
                  ) : null,
                )}
              </TriageCardTools>
            </TriageReconcileRowMirror>
          </div>
        ) : undefined
      }
      testIds={testIds}
    />
  );
}

/**
 * A card's line of chips, under its title - its panel's chip at its far end.
 * Inside another card, a line of words close under its title.
 */
function TriageCardTools({
  children,
  testId,
  isNested = false,
}: {
  children: ReactNode;
  testId?: string;
  isNested?: boolean;
}) {
  return (
    <div
      data-testid={testId}
      // Clear of the title, and across the row: the panel's chip at its
      // end stands under the amount.
      // A chip's height whether it has marks or not: every row is one
      // height, loading or loaded - a line of words its own.
      // Nothing on it - a chip with nothing to say draws nothing - no line.
      // Mirrored, its chips at its far end, as the rest of the card is.
      // In the half under a row, closer under its title, as tall as its
      // smaller chips.
      className={cn(
        "col-span-full flex items-center gap-1 min-w-0 max-w-full flex-wrap empty:hidden [[data-triage-mirrored]_&]:[flex-direction:row-reverse]",
        isNested ? "mt-0.5" : "mt-2.5 min-h-6.5",
        "[[data-triage-row-half]_&]:mt-1 [[data-triage-row-half]_&]:min-h-5",
      )}
    >
      {children}
    </div>
  );
}

export const TriageReconcileRow = memo(
  function TriageReconcileRow({
    item,
    isActive = false,
    rowStaging = null,
    isLoading = false,
    isCompact = false,
    becomes = null,
    onOpenPanel,
    linedUp = null,
    link = null,
  }: {
    item: TriageListItemFragment;
    /**
     * Its end of a link, on its edge: lined up beside a payment, the end of
     * the line from it; one of Similar's, the end of the line to what is
     * lined up with it - and, nothing lined up, whether it is ticked.
     */
    link?: TriageRowLink | null;
    /**
     * Lined up for a match, with what is in *business*: across two
     * businesses, that business's half of it, drawn under it in place of its
     * own.
     */
    linedUp?: {
      kind: TriageBulkMatch;
      business: TriageRowHalf["entity"] | null;
    } | null;
    isActive?: boolean;
    rowStaging?: TriageActiveRowStaging | null;
    /**
     * Where its Open in panel opens it, in place of the app's panel - or,
     * ``null``, it has none: one of Similar's, in the queue's place or lined
     * up with its documents (``TriageBookLikeCards``).
     */
    onOpenPanel?: ((identifier: string) => void) | null;
    /**
     * Listed beside one of its payee's about to be booked: what it becomes,
     * in place of its own suggestions (``TriageBookLikeCards``).
     */
    becomes?: TriageRowBecomes | null;
    /**
     * Its two lines alone - whose and on which account, then what and how
     * much - with none of its chips: the payment a phone's matches are for,
     * named under them.
     */
    isCompact?: boolean;
    /**
     * The row stands in for one while the queue loads: the same row, each
     * piece wrapped in ``Skeleton``, so it is the size of the row it
     * stands for.
     */
    isLoading?: boolean;
  }) {
    const detailContext = toRowDetailBridge(rowStaging, item.identifier);
    return (
      <TriageReconcileRowChrome
        item={item}
        isActive={isActive}
        isLoading={isLoading}
        isCompact={isCompact}
        detailContext={detailContext}
        becomes={becomes}
        onOpenPanel={onOpenPanel}
        linedUp={linedUp}
        link={link}
      />
    );
  },
  (prev, next) => {
    if (
      prev.link?.link !== next.link?.link ||
      prev.link?.end !== next.link?.end ||
      prev.link?.isEngaged !== next.link?.isEngaged ||
      (prev.link?.onPress == null) !== (next.link?.onPress == null) ||
      prev.link?.label !== next.link?.label ||
      prev.linedUp?.kind !== next.linedUp?.kind ||
      prev.linedUp?.business?.identifier !==
        next.linedUp?.business?.identifier ||
      prev.isLoading !== next.isLoading ||
      prev.isCompact !== next.isCompact ||
      prev.becomes?.now !== next.becomes?.now ||
      prev.becomes?.like !== next.becomes?.like ||
      JSON.stringify(prev.becomes?.business ?? null) !==
        JSON.stringify(next.becomes?.business ?? null) ||
      prev.becomes?.document?.label !== next.becomes?.document?.label
    ) {
      return false;
    }
    // Its hold, which changes on its own: held or not, why, and whose word it
    // waits on.
    const isHeldTheSame =
      prev.item.triageHeldAt === next.item.triageHeldAt &&
      prev.item.triageHoldReason === next.item.triageHoldReason &&
      prev.item.triageHeldForViewer === next.item.triageHeldForViewer;
    if (
      prev.item.payeePaymentCount !== next.item.payeePaymentCount ||
      prev.item.tags !== next.item.tags
    ) {
      return false;
    }
    if (prev.isActive || next.isActive) {
      return (
        prev.isActive === next.isActive &&
        prev.item.id === next.item.id &&
        prev.item.suggestions === next.item.suggestions &&
        prev.item.isProcessing === next.item.isProcessing &&
        isHeldTheSame &&
        prev.rowStaging === next.rowStaging
      );
    }
    return (
      prev.item.id === next.item.id &&
      prev.item.suggestions === next.item.suggestions &&
      prev.item.isProcessing === next.item.isProcessing &&
      isHeldTheSame &&
      // Moved, or settled another way: its other business's half drawn again.
      prev.item.belongsToEntity === next.item.belongsToEntity &&
      prev.item.settlementTreatment === next.item.settlementTreatment
    );
  },
);

function TriageReconcileRowChrome({
  item,
  isActive,
  isLoading,
  isCompact,
  detailContext,
  becomes,
  onOpenPanel,
  linedUp,
  link,
}: {
  item: TriageListItemFragment;
  link: TriageRowLink | null;
  isActive: boolean;
  isLoading: boolean;
  isCompact: boolean;
  detailContext: TriageRowDetailBridge | null;
  becomes: TriageRowBecomes | null;
  onOpenPanel?: ((identifier: string) => void) | null;
  linedUp: {
    kind: TriageBulkMatch;
    business: TriageRowHalf["entity"] | null;
  } | null;
}) {
  const { enterSuggestions, focusTriageList, openSimilar } =
    useTriageViewActions();
  // Its line of tools, and its tags on it, unless the person has hidden
  // them from the queue's eye.
  const isToolsShown = State(
    (state) => !state.hiddenTriageRowParts.includes("tools"),
  );
  const isTagsShown = State(
    (state) => !state.hiddenTriageRowParts.includes("tags"),
  );
  const formattedDate = Utils.fDate(new Date(item.effectiveDate));
  const formattedAmount = Utils.fCurrency(item.amount, false, true);
  const financialAccount = item.financialAccount;
  const bankAccountName = financialAccount?.name;
  const financialAccountNodeId = financialAccount?.id;
  const isMatchingDetail =
    isActive &&
    detailContext != null &&
    detailContext.triage.identifier === item.identifier;
  const rowEntity = isMatchingDetail
    ? detailContext.triage.entity
    : item.entity;
  const entityName =
    rowEntity?.name ??
    State.getState().entities.find(
      (entity) => entity.identifier === rowEntity?.identifier,
    )?.name ??
    null;
  const entityNodeId = rowEntity?.id;
  // That business's half grows in under the payment as a suggestion for it is
  // picked, and shrinks away as it is let go - as a suggestion's card opens
  // and shuts - showing what it showed until it has gone. One there as the
  // row is drawn is simply there.
  // What it is there, said on that half: how the two settle it - as the open
  // payment's Move says, or as it was saved; as coded by hand, once asked -
  // from the business that paid it, or to the one that took it in. Where none
  // of them says, only who paid: never a guess, so open or not it says the
  // same. As saved, it is the row's own - the open detail is the queue's row
  // stood in while its details load, and says nothing of it.
  const isStagedByHand =
    isMatchingDetail &&
    detailContext.selections.settlementEntityIdentifier != null &&
    detailContext.selections.settlementEntityIdentifier !==
      rowEntity?.identifier;
  // Open, a suggestion picked that settles in another business says how,
  // where nothing else does: the way its card recommends, as the server books
  // it - not a way the screen makes up.
  const settledAs = isStagedByHand
    ? detailContext.selections.settlementTreatment
    : ((isMatchingDetail
        ? detailContext.selections.settlementTreatment
        : null) ??
      item.settlementTreatment ??
      (isMatchingDetail ? detailContext.recommendedTreatment : null) ??
      null);
  const isOut = Number(item.amount ?? 0) < 0;
  // How the two settle it, in words: from the business that paid it, or to
  // the one that took it in; unsaid, only who paid - that business named
  // where it has a name.
  const sayTreatment = (treatment: SettlementTreatmentEnum | null) =>
    `${
      treatment
        ? {
            [SettlementTreatmentEnum.Loan]: "Loan",
            [SettlementTreatmentEnum.OwnerEquity]: "Equity",
            [SettlementTreatmentEnum.Dividend]: "Dividend",
          }[treatment]
        : isOut
          ? "Paid"
          : "Received"
    }${
      entityName
        ? ` ${treatment ? (isOut ? "from" : "to") : "by"} ${entityName}`
        : ""
    }`;
  // Lined up across two businesses, the other's half as the match books it
  // there: a transfer from it or to it - how the two settle it, as the server
  // books it (``recommendedTreatment``), never "a transfer", which settles
  // nothing; the same charge on that business's feed, a document it paid.
  // None where it is not across two, or is moved there already, its own half
  // saying so.
  const linedIn = linedUp?.business;
  const transferSettledAs =
    (item.suggestions ?? []).find(
      (suggestion) =>
        suggestion.job === "TRANSFER" &&
        suggestion.status === TriageSuggestionStatusEnum.Pending,
    )?.recommendedTreatment ?? null;
  const half: TriageRowHalf | null =
    linedUp &&
    linedIn?.identifier &&
    linedIn.identifier !== item.entity?.identifier &&
    linedIn.identifier !== item.belongsToEntity?.identifier
      ? {
          entity: linedIn,
          says:
            linedUp.kind === "transfers"
              ? sayTreatment(transferSettledAs)
              : linedUp.kind === "duplicates"
                ? entityName
                  ? `Also on ${entityName}'s feed`
                  : "Also on its own feed"
                : sayTreatment(null),
        }
      : null;
  const halfSays = half ? half.says : sayTreatment(settledAs);
  // The change picked staged on its half - another business it would be for,
  // or another way the two would settle it: what it is, struck out, and what
  // it becomes; one it is for anew, all to come. Picked in the bulk list
  // (*becomes*), or - the payment open - the suggestion picked for it, or what
  // is coded by hand, against what was saved.
  const pickedEntity =
    isMatchingDetail && !half
      ? resolveRowSettlementEntity(item, detailContext, isMatchingDetail)
      : null;
  const business =
    becomes?.business ??
    (isMatchingDetail && !half
      ? {
          now: item.belongsToEntity
            ? {
                id: item.belongsToEntity.id,
                identifier: item.belongsToEntity.identifier,
                name: item.belongsToEntity.name ?? null,
                treatment: item.settlementTreatment ?? null,
              }
            : null,
          like: pickedEntity
            ? {
                id: pickedEntity.id,
                identifier: pickedEntity.identifier,
                name: pickedEntity.name ?? null,
                treatment: settledAs,
              }
            : null,
        }
      : null);
  const isBusinessStaged =
    business != null &&
    ((business.now?.identifier ?? null) !==
      (business.like?.identifier ?? null) ||
      (business.now?.treatment ?? null) !== (business.like?.treatment ?? null));
  const stagedBusiness: TriageRowHalfStaged | null = isBusinessStaged
    ? {
        was: business.now
          ? {
              name: business.now.name,
              says: sayTreatment(business.now.treatment),
            }
          : null,
        becomes: business.like
          ? {
              name: business.like.name,
              says: sayTreatment(business.like.treatment),
            }
          : null,
      }
    : null;
  const settlementEntity = isBusinessStaged
    ? toBelongsToEntity(business.like ?? business.now)
    : half
      ? toBelongsToEntity(half.entity)
      : isMatchingDetail
        ? pickedEntity
        : resolveRowSettlementEntity(item, detailContext, isMatchingDetail);
  const [mirrorEntity, setMirrorEntity] = useState(settlementEntity);
  const [mirrorSays, setMirrorSays] = useState(halfSays);
  // Staged, held as its words are - as it shrinks away, what it showed.
  // Or lined up across two businesses for a match that books in the other - a
  // transfer's other side, a document paid there - all to come; a copy's half
  // is there already, said as it is.
  const staged: TriageRowHalfStaged | null =
    stagedBusiness ??
    (half && linedUp?.kind !== "duplicates"
      ? {
          was: null,
          becomes: { name: half.entity?.name ?? null, says: half.says },
        }
      : null);
  const [mirrorStaged, setMirrorStaged] = useState(staged);
  if (
    settlementEntity &&
    JSON.stringify(staged) !== JSON.stringify(mirrorStaged)
  ) {
    setMirrorStaged(staged);
  }
  const [isMirrorOpen, setIsMirrorOpen] = useState(settlementEntity != null);
  const mirrorSlotRef = useRef<HTMLDivElement | null>(null);
  if (
    settlementEntity &&
    (settlementEntity.identifier !== mirrorEntity?.identifier ||
      settlementEntity.name !== mirrorEntity?.name)
  ) {
    setMirrorEntity(settlementEntity);
  }
  if (settlementEntity && halfSays !== mirrorSays) {
    setMirrorSays(halfSays);
  }
  const hasSettlementEntity = settlementEntity != null;
  useLayoutEffect(() => {
    if (hasSettlementEntity === isMirrorOpen) return;
    if (hasSettlementEntity) {
      // Laid out shut first, so it has a height to grow from.
      mirrorSlotRef.current?.getBoundingClientRect();
      setIsMirrorOpen(true);
      return;
    }
    setIsMirrorOpen(false);
    // Nothing moves for someone who has asked for no motion: gone at once.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setMirrorEntity(null);
    }
  }, [hasSettlementEntity, isMirrorOpen]);
  // List scope owning keyboard (j/k / Enter) is not the same as the user
  // having dived into this row. Row controls must stay ``tabIndex={-1}`` until
  // focus is actually inside the row, otherwise they steal Enter from the list.
  const [isRowFocusActive, setIsRowFocusActive] = useState(false);
  const keyboardActive = isActive && isRowFocusActive;

  const handleRowFocusCapture = (event: FocusEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) {
      setIsRowFocusActive(true);
    }
  };

  const handleRowBlurCapture = (event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget as Node | null;
    if (!next || !event.currentTarget.contains(next)) {
      if (next instanceof Element) {
        const listNode = findNearestContextNode(event.currentTarget);
        const nextNode = findNearestContextNode(next);
        if (listNode && nextNode && isAncestorOrSelf(listNode, nextNode)) {
          return;
        }
        if (listNode && hasOpenResetHotkeysOverlayInSubtree(listNode)) {
          const overlayNode = findDeepestOpenResetHotkeysDescendant(listNode);
          if (
            overlayNode &&
            nextNode &&
            isAncestorOrSelf(overlayNode, nextNode)
          ) {
            return;
          }
        }
      }
      setIsRowFocusActive(false);
    }
  };

  useEffect(() => {
    if (!isActive && isRowFocusActive) {
      setIsRowFocusActive(false);
    }
  }, [isActive, isRowFocusActive]);

  const handleRowKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      const contextNode = findNearestContextNode(event.currentTarget);
      if (hasOpenResetHotkeysOverlayInSubtree(contextNode)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      // Inside the row: return to list focus, keep selection.
      focusTriageList();
      return;
    }

    const direction =
      event.key === "ArrowDown" ||
      event.key === "ArrowRight" ||
      (event.key === "Tab" && !event.shiftKey)
        ? "down"
        : event.key === "ArrowUp" ||
            event.key === "ArrowLeft" ||
            (event.key === "Tab" && event.shiftKey)
          ? "up"
          : null;
    if (!direction) {
      return;
    }

    const result = moveTriageRowFocus(event.currentTarget, direction);
    if (result === "moved") {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (result === "at-end" && event.key === "ArrowRight" && isActive) {
      event.preventDefault();
      event.stopPropagation();
      enterSuggestions(item.id);
      return;
    }
    if (result === "at-start" && direction === "up") {
      event.preventDefault();
      event.stopPropagation();
      focusTriageList();
    }
    if (result === "at-end" && event.key === "Tab" && !event.shiftKey) {
      // Fall through to TriageView Tab → search.
      return;
    }
    if (result === "at-end" || result === "at-start") {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // A payment another business's is worked on in that business's books: its
  // documents, its ways to book and its tags are its half's, there - where
  // its half is that business's, not another's it is lined up with, and it
  // stays there: about to be booked back in its own, its half going, they are
  // its own row's.
  const isWorkedThere =
    item.belongsToEntity != null &&
    mirrorEntity?.identifier === item.belongsToEntity.identifier &&
    (!isBusinessStaged ||
      business.like?.identifier === item.belongsToEntity.identifier);
  // The open payment's Move - M opens it - on its own row, the payment's:
  // not its half's, where its tools are once it is another business's.
  const moveChip =
    isMatchingDetail && !isLoading && !detailContext.isSimilar ? (
      <TriageMovePayment
        triage={detailContext.triage}
        selections={detailContext.selections}
        movePayment={detailContext.movePayment}
        movingPaymentTo={detailContext.movingPaymentTo}
        isProcessing={detailContext.isProcessing}
      />
    ) : item.belongsToEntity?.name && !isLoading ? (
      // Not open, moved: the business it is for all the same, on the chip
      // every other business is drawn on - its Move is the open one's.
      <TriageMovedChip testId="triage-row-moved-to" payment={item} />
    ) : null;
  // The open payment's payee's others, offered as its other chips are: on
  // offer, so dashed - pressed (or L), the queue narrowed to them.
  const similarCount = item.payeePaymentCount ?? 0;
  const similarChip =
    isMatchingDetail && !isLoading && !detailContext.isSimilar ? (
      <Badge
        asChild
        variant="outline"
        data-chip-state="suggested"
        className={cn(
          TRIAGE_ROW_FOOT_CHIP_CLASS,
          TRIAGE_ROW_CHIP_STATE_CLASS.suggested,
          "gap-1 cursor-pointer",
        )}
      >
        <button
          type="button"
          aria-label={`Similar to ${item.description ?? "this payment"}`}
          data-testid="triage-similar-offer"
          // A press of the row's own: the keyboard left where it was.
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            openSimilar?.();
          }}
        >
          <Icon ReactIcon={LayersIcon} size="xs" />
          {similarCount > 0 ? `Similar (${similarCount})` : "Similar"}
          <HotkeyDecorator
            hotkey={TRIAGE_KEYS.BOOK_LIKE}
            size="xs"
            color="inherit"
          />
        </button>
      </Badge>
    ) : null;
  // The payment's own record, in the panel: a little chip of its own at the
  // end of the payment's own line of tools - the top row's, whosever half it
  // is worked on in.
  const openPanelButton =
    onOpenPanel === null ? null : (
      <Skeleton
        loading={isLoading}
        variant="rectangular"
        className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit] rounded-lg"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="secondary"
              size="icon-xs"
              aria-label="Open in panel"
              data-testid="triage-row-open-panel"
              // A control of the row's own: opening the payment is not
              // picking it.
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                if (onOpenPanel) {
                  onOpenPanel(item.identifier);
                  return;
                }
                const nodeId = identifierAndTypenameToId(
                  item.identifier,
                  "TransactionObject",
                );
                const state = State.getState();
                // Showing already, it puts it away.
                if (
                  state.isNodePanelOpen &&
                  state.activePanelNodeId === nodeId
                ) {
                  state.setIsNodePanelOpen(false);
                  return;
                }
                state.openNodePanel(nodeId, { autofocus: false });
              }}
              className="text-muted-foreground/80"
            >
              <Icon ReactIcon={PanelRightOpenIcon} size="xs" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Open in panel</TooltipContent>
        </Tooltip>
      </Skeleton>
    );
  // Its row of tools: what is offered it, its tags, and its panel's button.
  const toolRow =
    isCompact || !isToolsShown ? null : (
      <>
        <Skeleton
          loading={isLoading}
          variant="rectangular"
          className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit] rounded-lg"
        >
          <TriageRowSuggestionChips item={item} becomes={becomes} />
        </Skeleton>
        {isWorkedThere ? null : moveChip}
        {/* Similar on the payment's own line, never its half's. */}
        {isWorkedThere ? null : similarChip}
        {/* The payment's own record, in the panel: a little chip of its
          own at the line's end, the panel's mark alone - its tags beside
          it. Its title is only its title - a tap on it picks the row, as
          anywhere on it does. */}
        <div className="ml-auto flex items-center gap-1 min-w-0 [[data-triage-mirrored]_&]:ml-0 [[data-triage-mirrored]_&]:[margin-right:auto]">
          {isLoading || !isTagsShown ? null : (
            <TriageRowTags item={item} detailContext={detailContext} />
          )}
          {isWorkedThere ? null : openPanelButton}
        </div>
      </>
    );
  return (
    <div
      data-testid="triage-reconcile-row"
      data-triage-id={item.id}
      onKeyDown={handleRowKeyDown}
      onFocusCapture={handleRowFocusCapture}
      onBlurCapture={handleRowBlurCapture}
      className="w-full flex flex-col box-border"
    >
      <TriageCard
        isLoading={isLoading}
        keyboardActive={keyboardActive}
        marker={
          link?.end === "to" ? (
            <TriageLinkBall
              link={link.link}
              end="to"
              isEngaged={link.isEngaged}
              onPress={link.onPress}
              label={link.label}
              testId={link.testId}
            />
          ) : undefined
        }
        // Its end of a link on its right edge - or, the open payment with a
        // suggestion picked for it, the end of the line to that
        // (``TriageLinks``).
        markerEnd={
          link?.end === "from" ? (
            <TriageLinkBall
              link={link.link}
              end="from"
              isEngaged={link.isEngaged}
              onPress={link.onPress}
              label={link.label}
              testId={link.testId}
            />
          ) : isMatchingDetail &&
            !detailContext.isSimilar &&
            detailContext.selections.linesSuggestionIdentifier != null ? (
            <TriageLinkBall
              link="picked"
              end="from"
              isEngaged
              testId="triage-picked-link-ball"
            />
          ) : undefined
        }
        crumbs={[
          ...(entityName
            ? [
                {
                  nodeId: entityNodeId,
                  label: entityName,
                  testId: "triage-row-entity",
                },
              ]
            : []),
          ...(bankAccountName
            ? [
                {
                  nodeId: financialAccountNodeId,
                  label: bankAccountName,
                  testId: "triage-row-bank-account",
                },
              ]
            : []),
        ]}
        date={formattedDate}
        title={item.description}
        amount={formattedAmount}
        // Its row of tools, as the queue's eye says - a card of the payee's
        // others, with Similar on, the same - here, unless the payment is
        // another business's: then on its half, its Move and its panel's
        // button alone here.
        tools={
          isWorkedThere ? (
            (moveChip || similarChip || openPanelButton) &&
            !isCompact &&
            isToolsShown ? (
              <>
                {moveChip}
                {similarChip}
                <div className="ml-auto flex [[data-triage-mirrored]_&]:ml-0 [[data-triage-mirrored]_&]:[margin-right:auto]">
                  {openPanelButton}
                </div>
              </>
            ) : null
          ) : (
            toolRow
          )
        }
      />
      {/* The half paid on the other entity's behalf, so it reads as part of
        the payment above even on a row not selected. */}
      {mirrorEntity && !isCompact ? (
        <div
          ref={mirrorSlotRef}
          data-testid="triage-row-mirror-slot"
          onTransitionEnd={(event) => {
            if (
              event.target === event.currentTarget &&
              event.propertyName === "grid-template-rows" &&
              !isMirrorOpen
            ) {
              setMirrorEntity(null);
            }
          }}
          className={cn(
            "grid",
            isMirrorOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            "transition-normal [transition-duration:240ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] delay-[0s] [transition-property:grid-template-rows] motion-reduce:transition-normal [@media(prefers-reduced-motion:_reduce)]:[transition-duration:0s] [@media(prefers-reduced-motion:_reduce)]:[transition-timing-function:ease] motion-reduce:delay-[0s] motion-reduce:transition-none",
          )}
        >
          {/* Everything that takes room is in here, so shut it is nothing. */}
          <div className="min-h-0 overflow-hidden">
            <TriageReconcileRowMirror
              settlementEntity={mirrorEntity}
              description={mirrorSays}
              staged={mirrorStaged}
              formattedAmount={formattedAmount}
              formattedDate={formattedDate}
            >
              {isWorkedThere && toolRow ? (
                <TriageCardTools testId="triage-row-mirror-tools">
                  {toolRow}
                </TriageCardTools>
              ) : null}
            </TriageReconcileRowMirror>
          </div>
        </div>
      ) : null}
    </div>
  );
}
